import { ReactElement } from 'react'
import type { StringsDictionary } from 'lang'
import { clientDetail, clientInfo } from 'configureBrand'
import { SecureLink } from 'ui/organisms/Footer/subcomponents'
import {
  AddressWrapper,
  QuestionLink,
  Text
} from 'ui/molecules/FrequentlyAskedQuestions/subcomponents'
import { Body as CLBody } from 'syf-component-library/ui/typography'
import { Link } from 'syf-component-library/ui/atoms'
import { Inset } from 'syf-component-library/ui/spacing'
import { cities, stateList } from 'const/states'
import {
  getInlineBusinessHours,
  shouldShowQuestion
} from 'helpers/brandHelpers'
import routePaths from 'const/routePaths'
import { stringToDateFormat } from 'helpers/fieldFormatters'
import { userAccountInfo } from 'helpers/user'
import {
  Body,
  CustomerServiceHours,
  HideReplacement,
  List
} from './subcomponents'

const {
  clientName,
  displayName,
  alternateDisplayName,
  customerSupport: {
    closeAccountService,
    contactUsBrcDc,
    contactUsBrcPlcc,
    contactUsDirectCustomer,
    contactUsLba,
    contactUsProx,
    creditServiceNumber,
    getCSRNumber,
    systemErrorPageContacts
  },
  address: { payment, businessPayments }
} = clientInfo

const {
  storeName,
  payableAccountAcronym,
  address: { brcLegalNameChange }
} = clientDetail

/**
 * Prefixes are used for secure messages. (see `strings.secureMessage` below)
 * Each brand may have a distinct label (used to display text to the user) and
 * potentially a different value (used for api calls). Whenever the label and
 * value are the same, only the value needs to be given.
 *
 * Omit the .*Label fields when the value and label are the same.
 *
 * Eventually, these may need to be moved into brand specific translation
 * modules should they be implemented.
 */
const allSecureMessageBrandPrefixes: {
  readonly [key in ClientKeys]: {
    readonly brcLabel?: string
    readonly brcValue: string

    readonly dcLabel?: string
    readonly dcValue: string

    readonly proxLabel?: string
    readonly proxValue: string
  }
} = {
  flooranddecor: {
    brcLabel: '',
    brcValue: 'Floor and Decor Pro Premier',
    // no prox or dc configuration for floor and decor
    dcLabel: '',
    dcValue: '',
    proxLabel: '',
    proxValue: ''
  },
  lowes: {
    brcLabel: '',
    brcValue: `${displayName} Business`,
    // no dc configuration for floor and decor
    dcLabel: '',
    dcValue: '',
    proxLabel: `${displayName} Commercial`,
    proxValue: `${displayName} Account Receivable`
  },
  sams: {
    dcLabel: `${displayName} Business MasterCard`,
    dcValue: 'Sams Club Business MasterCard',
    brcLabel: `${displayName} Business Credit`,
    brcValue: 'Sams Club Business Credit',
    proxLabel: '',
    proxValue: `${displayName} Direct Credit`
  },
  p66: {
    // temporary configuration for p66
    brcLabel: '',
    brcValue: 'P66',
    dcLabel: '',
    dcValue: '',
    proxLabel: '',
    proxValue: ''
  },
  // projectX key is pocnci
  pocnci: {
    dcLabel: '',
    dcValue: '',
    brcLabel: '',
    brcValue: 'Project X',
    proxLabel: '',
    proxValue: ''
  },
  jcpenney: {
    dcLabel: '',
    dcValue: '',
    brcLabel: '',
    brcValue: 'JC Penney',
    proxLabel: '',
    proxValue: ''
  }
}

// get the message prefixes for the current client
const secureMessageBrandPrefixes = allSecureMessageBrandPrefixes[clientName]

const requestLimitChange = 'Request a Credit Limit Change'

const strings: StringsDictionary = {
  words: {
    or: 'or',
    Decrease: 'Decrease',
    Increase: 'Increase'
  },
  invoiceCreditSection: {
    'Applied Credit(s)': 'Applied Credit(s)',
    'Selected Invoice(s)': 'Selected Invoice(s)'
  },
  videoGallery: {
    navigateSummary: 'Navigate the Summary Screen',
    title: 'Walkthrough Videos',
    videoWalkthroughs:
      'Here you can view a collection of video walkthroughs, showing how to use key features of this platform.'
  },
  user: {
    deactivatingUser:
      'Deactivating this user will log them out of their current session. The user will no longer be able to log in or access their online account.',
    reactivatingAccount: `In order to reactivate, you must contact ${displayName} Credit Services.`,
    wantToDeactivateAcc: 'Are you sure you want to deactivate',
    account: 's account?',
    resettingPassword:
      'Resetting this user’s password will log them out of their current session. Their old password will no longer work, and you will be provided a temporary password to pass along to this user.',
    wantToResetPassword: 'Are you sure you want to reset',
    password: 's password?',
    changeUserIdTitle: 'Change the User ID you use to log in to your account.',
    updatedUserIdConfirmMessage:
      'Your User ID has been updated to %currentUserId%. Please login again to update any additional information.',
    'If you don’t remember your USER ID':
      'If you don’t remember your USER ID, you can recover it'
  },
  buyer: {
    add: 'Add',
    addAnotherBuyer: 'Add Another Buyer',
    addAuthorizedBuyer: 'Add an Authorized Buyer',
    addBuyer: 'Add Buyer',
    addNewBuyer: 'Add a New Buyer',
    addNewBuyerAccountsDescription: "Add New Buyer's Assigned Accounts",
    addNewBuyerInstructions:
      'An Authorized Buyer has the ability to make purchases on any accounts assigned to them. Enter the Buyer’s name and contact information, assign account permissions, and decide if the Buyer should be sent a card.',
    addNewBuyerSendCardsDescription:
      "Select Cards to Send to Buyer's Assigned Accounts",
    'New Buyer Added': 'New Buyer Added',
    '%firstName% %lastName% needs additional verification.':
      '%firstName% %lastName% needs additional verification.',
    '%firstName% %lastName% was NOT added as an Authorized Buyer to all accounts':
      '%firstName% %lastName% was NOT added as an Authorized Buyer to all accounts',
    '%firstName% %lastName% was successfully added as an Authorized Buyer.':
      '%firstName% %lastName% was successfully added as an Authorized Buyer.',
    'Our system indicates buyer - %firstName% %lastName% is added duplicate to the following accounts':
      'Our system indicates buyer - %firstName% %lastName% is added duplicate to the following accounts:',
    'Effective Immediately %firstName% will be able to make purchases at any displayName store with a picture ID. However, it may take up to 48 hours before the update is reflected in your account’s buyer list.': `Effective Immediately %firstName% will be able to make purchases at any ${displayName} store with a picture ID. However, it may take up to 48 hours before the update is reflected in your account’s buyer list.`,
    manageAuthBuyers: 'Manage Authorized Buyers',
    numberOfAccountsExceedsLimit:
      'Number of accounts selected has exceeded the 5 limit.',
    printRangeOfBuyers: 'Print Range of buyers',
    retryAddingBuyer: 'Retry Adding Buyer',
    returnToManageBuyers: 'Return to Manage Buyers',
    'The following people are authorized to make purchases to this account and its Child accounts':
      'The following people are authorized to make purchases to this account and its Child accounts.',
    'New buyer additions may take up to 48 hours to appear on the list below':
      'New buyer additions may take up to 48 hours to appear on the list below.',
    'You may select only 5 buyers at a time to request for replacing card or removing from account.':
      'You may select only 5 buyers at a time to request for replacing card or removing from account.',
    selectUpTo5Accounts: 'Select up to 5 Accounts.',
    'Authorized Buyer Information': 'Authorized Buyer Information',
    'Company-Owned': 'Company-Owned',
    Personal: 'Personal',
    'Not Sure': 'Not Sure',
    'Mobile Phone': 'Mobile Phone',
    'You must provide an email address and/or mobile phone number.':
      'You must provide an email address and/or mobile phone number.',
    'You authorize Synchrony Bank to contact the phone number...':
      'You authorize Synchrony Bank to contact the phone number provided. By providing a cell phone number, you agree on behalf of the Authorized Buyer to receive Account updates and information, including text messages from %brandName% and Synchrony Bank.',
    'All fields are required except those marked optional':
      'All fields are required except those marked optional',
    'Changes to this buyer’s information have been saved.':
      'Changes to this buyer’s information have been saved.',
    'There was a problem updating Authorized Buyer Information. Please re-try or contact customer care at': `There was a problem updating Authorized Buyer Information. Please re-try or contact customer care at %customerCare%`,
    'There was a problem updating Authorized Buyer Information. Please re-try or contact Customer Service at': `There was a problem updating Authorized Buyer Information. Please re-try or contact %displayName% at %customerCare%`,
    'Phone Number Type': 'Phone Number Type',
    'The selected buyers have been removed, effective immediately. Please...':
      'The selected buyers have been removed, effective immediately. Please remember that this change may take up to 24 hours to display in your buyer list.',
    'Deleted Buyers': 'Deleted Buyers',
    orderCards: (): ReactElement => (
      <>
        Order <HideReplacement>Replacement</HideReplacement> Card
      </>
    ),
    Selected: 'Selected',
    verification: (): ReactElement => (
      <>
        To comply with the Federal Patriot Act, we are required to verify
        additional information regarding the authorized buyer being added.
        Please obtain the authorized buyer&apos;s date of birth and address and
        then call our Account Management Team at{' '}
        {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)} so that
        we can expedite the Authorized Buyer Verification process.
      </>
    ),
    removeBuyer: ({ noOfSelectedBuyers }) => (
      <>Remove {noOfSelectedBuyers} Buyer</>
    ),
    removeBuyers: ({ noOfSelectedBuyers }) => (
      <>Remove {noOfSelectedBuyers} Buyers</>
    )
  },
  addNewUser: {
    'User already exist': 'User already exists.',
    'Online access activated':
      'online access was successfully activated. Please share the below user ID and temporary password with this person so that they can log in.',
    'User ID': 'User ID:',
    'Temporary Password': 'Temporary Password:',
    'Add New User': 'Add New User',
    'User will have access to online portal':
      'Adding a new User on this account provides them access to this online portal and account permissions based on what you assign below.',
    'Enter user information':
      'Enter User information and set their Account Privileges. If possible, provide an email address and phone number specific to the user. You can edit a User’s Account Privileges at any time.',
    'User Information': 'User Information'
  },
  needAssistance: {
    title: 'Need assistance?',
    businessHours: 'Business Hours'
  },
  contactUs: {
    'Contact Us': 'Contact Us',
    'For questions or inquiries regarding your account please call':
      'For questions or inquiries regarding your account please call:'
  },
  clientInfo: {
    'Monday - Friday': 'Monday - Friday',
    'Saturday and Sunday': 'Saturday and Sunday',
    'Saturday & Sunday': 'Saturday & Sunday',
    Saturday: 'Saturday',
    Sunday: 'Sunday',
    'Sunday closed': 'Sunday closed',
    'Monday - Friday 07:00 AM - 07:00 PM EST':
      'Monday - Friday 07:00 AM - 07:00 PM EST',
    'Monday - Friday 09:00 AM - 10:30 PM EST':
      'Monday - Friday 09:00 AM - 10:30 PM EST',
    'Saturday 09:00 AM - 09:00 PM EST': 'Saturday 09:00 AM - 09:00 PM EST',
    'Saturday 10:00 AM - 06:30 PM EST': 'Saturday 10:00 AM - 06:30 PM EST',
    '24 hours / 7 days a week': '24 hours / 7 days a week',
    '24 hours a day, 7 days a week': '24 hours a day, 7 days a week',
    '07:00 AM - 07:30 PM ET': '07:00 AM - 07:30 PM ET',
    '08:00 AM - 01:00 AM EST': '08:00 AM - 01:00 AM EST',
    '09:00 AM - 10:00 PM EST': '09:00 AM - 10:00 PM EST',
    '08:00 AM - 10:00 PM ET': '08:00 AM - 10:00 PM ET',
    '10:00 AM - 09:00 PM ET': '10:00 AM - 09:00 PM ET',
    lowesBusinessHoursInline: '24 hours a day / 7 days a week',
    samsBusinessHoursInline:
      'Monday-Saturday 09:00 AM - 10:30 PM EST, Sunday 09:00AM - 09:00 PM EST',
    floorAndDecorBusinessHoursInline:
      'Monday-Friday 7:00am-10:00pm EST, Saturday 8:00am-10:00pm EST, Sunday 10:00am-9:00pm EST'
  },
  onlineUsers: {
    applyChildAccountPrivileges:
      'Apply Privileges to All Associated Child Accounts',
    userInfo: 'User Information',
    'You authorize Synchrony Bank to contact the phone number provided': `You authorize Synchrony Bank to contact the phone number provided. By providing a cell phone number, you agree on behalf of the Authorized User to receive Account updates and information, including text messages from ${alternateDisplayName} and Synchrony Bank.`,
    'One-time Passcode': 'One-time Passcode',
    'Do not require a one-time passcode for this user':
      'Do not require a one-time passcode for this user.',
    'If the toggle is switched on, this user will skip the two-step verification process':
      'If the toggle is switched on, this user will skip the two-step verification process. Only switch the toggle on if the user is currently experiencing difficulty completing verification.',
    userUpdateSuccess:
      'Changes to this user’s Users Information have been saved.',
    tempPasswordSuccess: ({
      temporaryPassword
    }: {
      temporaryPassword: string
    }): ReactElement => (
      <>
        This user’s password was successfully reset. Temporary Password:{' '}
        {temporaryPassword}
      </>
    ),
    deactivateUserSuccess: (): ReactElement => {
      return (
        <Text aria-labelledby="resetPasswordText1 resetPasswordLink resetPasswordText2">
          <span id="resetPasswordText1">
            This user’s online access has been deactivated. Contact{' '}
          </span>
          <Link id="resetPasswordLink" isInline to={routePaths.contactUs}>
            Customer Care
          </Link>{' '}
          <span id="resetPasswordText2">to reactivate.</span>
        </Text>
      )
    },
    'Your child account privileges have been successfully updated...':
      'Your child account privileges have been successfully updated, effective immediately.'
  },
  authenticationSection: {
    authentication:
      'Enter the authentication code as it appears on a letter sent to you when you first opened the account, or on your most recent Billing Statement. This code is case-sensitive; please enter exactly as provided.',
    alert:
      'If you continue without an authentication code, a User ID and temporary password will be generated and mailed to the business address on file within 7 to 10 business days.',
    access:
      'You will not be able to access your account online until you have received your User ID and temporary password in the mail and complete registration.',
    contact: 'If you have any questions, please call Customer Care at'
  },
  cancelAutoPayConfirmation: {
    'You are no longer enrolled in AutoPay...':
      'You are no longer enrolled in AutoPay and any scheduled AutoPay payments have been cancelled. We recommend reviewing your current balance to avoid any late payments.',
    screen: 'screen.',
    submitted: 'Submitted',
    'Email Confirmation': 'Email Confirmation',
    'AutoPay payment series cancelled': 'AutoPay payment series cancelled.',
    'You may enroll back into AutoPay at any time...':
      'You may enroll back into AutoPay at any time by navigating to',
    'Make a Payment.': 'Make a Payment.'
  },
  cancelPaymentSummary: {
    referenceNumber: 'Reference No.',
    schedule: 'Scheduled for',
    amount: 'Amount',
    payTo: 'Pay To',
    payFrom: 'Pay From'
  },
  gridRow: {
    clickHereToExpand: 'Click here to expand',
    selectRowRecord: 'Select row record'
  },
  invoiceAmountColumn: {
    'Reset balance amount for invoice %invoiceNumber%':
      'Reset balance amount for invoice %invoiceNumber%',
    'Enter Balance amount for invoice %invoiceNumber%':
      'Enter Balance amount for invoice %invoiceNumber%'
  },
  transactionTable: {
    sku: 'SKU',
    description: 'Description',
    qty: 'Qty',
    unit: 'Unit',
    unitPrice: 'Unit Price',
    exPrice: 'Ex. Price',
    buyerSignature: 'Buyer Signature',
    signatureText:
      'Contact the store where the purchase was made to get a copy of the receipt with the buyer’s signature.',
    subtotal: 'Subtotal',
    tax: 'Tax',
    adjustments: 'Adjustments',
    invoiceTotal: 'Invoice Total',
    invoiceNo: 'Invoice No.',
    saleDate: 'Sale Date',
    poNoJobName: 'PO No. / Job Name',
    invoice: 'Invoice',
    poJob: 'PO/Job',
    store: 'Store',
    buyer: 'Buyer',
    status: 'Status',
    transactionDetails: 'Transaction Details',
    total: 'Total',
    'Invoice %invoiceNumber%': 'Invoice %invoiceNumber%'
  },
  termsAndConditions: {
    titleText: 'I understand that:'
  },
  payments: {
    daysPastDue: 'Days Past Due',
    amountPastDue: 'Amount Past Due'
  },
  statement: {
    'Looking for an earlier statement': 'Looking for an earlier statement?',
    'The 6 most recent statements are available':
      'The 6 most recent statements are available online, but you may request paper copies of statements up to 24 months old. Click the button below to start.',
    enrollIneStatements: 'Enroll in eStatements',
    viewYourStatements: 'to view your statements online.',
    electronicStatements:
      'Only statements processed after enrollment are available as Electronic Statements. Up to 12 months are available to view online.',
    copyOfStatement:
      'To have a copy of a statement sent to you, please choose Request a Paper Statement.',
    viewYourStatementOnline:
      'Enroll in eStatements to view your statements online.',
    pastStatements:
      'The past 6 months of statements will appear here. Only statements processed after enrollment are available to view as eStatements. To have a copy of a statement sent to you, please Request a Paper Statement.',
    selectStatementToView:
      'Select a statement to expand it and view details. Only statements processed after enrollment are available as Electronic Statements.',
    past12MonthStatements:
      'Up to 12 months are available to view online. If a statement is needed that is not shown, please Request a Paper Statement.',
    selectStatement: 'Select a statement to expand it and view details.',
    requestStatement: 'Request Statement',
    currentStatement: 'Current Statement',
    statementBalance: 'Statement Balance',
    minimumPayment: 'Minimum Payment',
    lastAmountPaid: 'Last Amount Paid',
    lastPaymentDate: 'Last Payment Date',
    nextPaymentDate: 'Next Payment Date',
    'Secondary/Job Account Statements': 'Secondary/Job Account Statements',
    'eStatements Terms and Conditions': 'eStatements Terms and Conditions',
    'I would like to continue receiving paper statements.':
      'I would like to continue receiving paper statements.',
    'I agree to the': 'I agree to the',
    'Terms & Conditions': 'Terms & Conditions',
    'Enroll in e-Statements': 'Enroll in e-Statements',
    'Change e-Statement Email Address': 'Change e-Statement Email Address',
    'Request A Paper Statement': 'Request A Paper Statement'
  },
  enrollStatements: {
    'By enrolling in electronic statements...':
      'By enrolling in electronic statements (eStatements), you will receive an email notification each time a new statement becomes available to view.',
    'Unless you indicate otherwise below':
      'Unless you indicate otherwise below,',
    'You will no longer receive paper statements in the mail':
      'you will no longer receive paper statements in the mail',
    'Only statements issued after your enrollment...':
      'Only statements issued after your enrollment in eStatements will show in your Statement History. Past statements that have not been sent electronically are not able to be displayed at this time.',
    'Parent accounts receiving two separate paper statements...':
      'Parent accounts receiving two separate paper statements (one for their own account and another consolidated statement for all their child accounts) will only receive the consolidated statement after enrolling in eStatements.'
  },
  showAuthenticationCodeModal: {
    title: 'Your Welcome Letter',
    'Image of Welcome Letter containing':
      'Image of Welcome Letter containing 8-character Authentication Code',
    'Image of Monthly Statement containing':
      'Image of Monthly Statement containing 8-character Authentication Code',
    enrollmentAuthCode:
      'A letter containing the authentication code was sent to the business address on file upon opening of the account. Look for the "Online Account Management Enrollment Authentication Code".',
    monthlyStatements: 'Your Monthly Statements',
    monthlyStatementsImageText:
      'The authentication code is also printed on each of your statements. Please refer to your most recent statement as the authentication code can change and older codes may not work.'
  },
  sessionExpired: {
    accountInactivity: ({
      isSessionExpired
    }: {
      isSessionExpired: boolean
    }): ReactElement => (
      <>
        To help keep your account secure, we close your session and log you out
        after {isSessionExpired ? '15 minutes' : '1 hour'} of inactivity.
      </>
    ),
    sessionTimeout: 'Session Timeout'
  },
  selectedAccounts: {
    headerText: `%noOfAccounts% Selected Accounts`,
    viewNoOfSelectedAccount: 'View %noOfAccounts% Selected Accounts'
  },
  secureSiteModal: {
    infoText:
      'Your account information, credit application and other sensitive information is being encrypted on this site. An encrypted item is scrambled so that no one between your computer and Synchrony Bank can read the message or the information you entered.',
    padlockIcon:
      'The reason the padlock icon provided on your browser may not appear locked (symbolizing an encrypted session) is because this site may use multiple frames to display information on your computer. The graphical header and navigation bar across the top of your screen is one frame. The selections that appear on the left are another frame. The main portion of the site appears in the remaining frame. Frames that display general information about the site, such as the navigation bar across the top of your screen, are not encrypted. However, any screen that displays or requests information about your account, user name, passcode, application and other sensitive information is encrypted.',
    confirmSteps:
      'To confirm that your information is encrypted perform the following three steps:',
    confidentialInfo: `Place your cursor on an area of screen where confidential information is being displayed. (a). With an Internet Explorer browser, right-click, select 'Properties.' (b). With a Firefox browser, right-click, select 'View Frame Info' or select 'View' | 'Page Frame Info' from the browser menu bar. (c). For Macintosh users, Ctrl-click, select 'New Window with this Frame' OR select 'View' | 'Page Info' from the browser menu bar. Check the Web address that you have accessed. If you are in a secure area, the address will appear as https://www. Notice the "s" in the address. This means that you have accessed a secure server.`
  },
  buyers: {
    title:
      'The following people will no longer be able to make purchases on this account.',
    removingAccess:
      'Removing access is immediate, but it may take up to 24 hours for this change to be reflected online in your buyer list.',
    selectedAccounts: 'Selected Accounts'
  },
  userPrivilege: {
    title: 'Manage Child Account Privileges',
    privilegeInfo:
      'Use this section to manage privileges across all of your child accounts. Please note, all changes will be applied equally across all accounts.',
    childAccDisplayTitle: 'Child Accounts Associated with this Account',
    childAccountPrivileges:
      'You can assign the following privileges to all the above Child accounts. Privileges cannot be distributed to individual Child accounts at this time.',
    beforeAssigningPrivilegesTitle:
      'Be sure to understand each privilege before assigning them.'
  },
  editPayment: {
    editPayment: 'Edit Payment',
    onetimePaymentCannotBeEdited:
      'A one-time payment cannot be edited due to payment amount exceeding current balance. Please cancel payment and re-schedule payment with valid amount.',
    forAssistancePleaseCall: (): ReactElement => (
      <>
        For assistance please call:{' '}
        {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)}
      </>
    ),
    youCanEditAccountAndDate:
      'You can edit the account to be charged and the scheduled payment date here. If you need to change the payment amount or other details, please cancel this payment and submit a new payment.'
  },
  payment: {
    'account was successfully added as a payment method':
      '%accountInfo% was successfully added as a payment method.',
    'Date scheduled cannot be in the past.':
      'Date scheduled cannot be in the past.',
    'Payment date cannot be more than 30 days in advance':
      'Payment date cannot be more than 30 days in advance.',
    'Payment dates & times are based on Eastern Time (ET)':
      'Payment dates & times are based on Eastern Time (ET).',
    'Add New Payment Account': 'Add New Payment Account',
    'Payment Total': 'Payment Total',
    '1 Select Payment Type': '1 Select Payment Type',
    paymentTransactionsHeaderTitle: 'Activity since Last Statement',
    listOfClearedPurchases:
      'You can find all cleared purchases and payments made since your last generated statement below. Transactions made in the last 24 hours may not be reflected.',
    referenceNo: 'Reference No.',
    status: 'Status',
    authorizationDate: ({
      shouldHaveLineBreak
    }: {
      shouldHaveLineBreak: boolean
    }): ReactElement => <>Authorization {shouldHaveLineBreak && <br />} Date</>,
    amount: 'Amount',
    authDate: 'Auth. Date',
    recentPayments: 'Recent payments made on this account will appear here.',
    cancelPayment: 'Cancel Payment',
    returnToViewPayments: 'Return to View Payments',
    cancelThisPaymentQuestion: 'Are you sure you want to cancel this payment?',
    paymentSuccessfullyUpdated:
      'Your pending payment has been successfully updated. Please allow up to 24 hours for the change to be reflected on your account.',
    paymentSuccessfullyCancelled:
      'Your pending payment has been successfully cancelled. Please allow up to 24 hours for the cancellation to be reflected on your account.',
    'Select the amount to pay':
      'Select the amount to pay, account to use, and date you want to make the payment.',
    'Select the amount to pay each cycle':
      'Select the amount to pay each cycle and which account to use.',
    'You can update your active payment account':
      'You can update your active payment account, review terms and conditions, and cancel AutoPay below.',
    'In order to change the amount':
      'In order to change the amount you are charged each month, please',
    'and return to the Payments screen':
      'and return to the Payments screen to set up a new AutoPay series.',
    'Review your payment details before proceeding':
      'Review your payment details before proceeding.',
    'A confirmation email will be sent...':
      'A confirmation email will be sent once the transaction has been submitted.',
    'I authorize Synchrony Bank to initiate the electronic payment...':
      'I authorize Synchrony Bank to initiate the electronic payment in the amount and using the banking account indicated above, and I authorize my bank to honor the withdrawals.',
    'Starting with your next billing cycle':
      'Starting with your next billing cycle, payments will be automatically scheduled when your payment is due.',
    'Next billing cycle will not be covered by AutoPay':
      'Your current balance, past due balance, and balance that accrues between now and your next billing cycle will not be covered by AutoPay and must be paid with a One-Time Payment.',
    'You can Schedule a One-Time Payment':
      'You can Schedule a One-Time Payment',
    here: 'here.',
    'Thank you for submitting a payment': 'Thank you for submitting a payment.',
    'Please allow up to 2 business days':
      'Please allow up to 2 business days for the payment to be reflected on your account.',
    Submitted: 'Submitted',
    'Schedule a One-Time Payment': 'Schedule a One-Time Payment',
    'Make a single payment':
      'Make a single payment. You choose the amount and payment date.',
    'Manage AutoPay Payments': 'Manage AutoPay Payments',
    'You can update, review and cancel AutoPay':
      'You can update your active payment account, review terms, and cancel the AutoPay series.',
    'Set up AutoPay': 'Set up AutoPay',
    'Automatically pay each pay period':
      'Automatically pay your statement balance, minimum payment, or an amount of your choosing each pay period.',
    'You are now enrolled in AutoPay': 'You are now enrolled in AutoPay',
    'Payment Confirmed': 'Payment Confirmed',
    'Payment Details': 'Payment Details',
    Type: 'Type',
    'Posted Date': 'Posted Date',
    'Selected Invoice(s) Total': 'Selected Invoice(s) Total'
  },
  autopay: {
    'Account was successfully added':
      '%accountName% was successfully added as a payment method.',
    'I acknowledge that I have read terms and conditions':
      'I acknowledge that I have read and that I agree to the terms and conditions for AutoPay payments as set forth by Synchrony Bank.',
    'Are you sure you want to cancel your current AutoPay series?':
      'Are you sure you want to cancel your current AutoPay series? You can set up AutoPay again any time in the future.',
    'Your payment will be auto-scheduled for when your next payment is due':
      'Your payment will be auto-scheduled for when your next payment is due.',
    'Payment date cannot be more than 30 days in advance':
      'Payment date cannot be more than 30 days in advance.',
    'Date scheduled cannot be in the past.':
      'Date scheduled cannot be in the past.',
    'Your AutoPay payment method was updated successfully':
      'Your AutoPay payment method was updated successfully. Please allow up to 24 hours for the change to be reflected on your account.',
    'Terms And Conditions': 'Terms and Conditions',
    'manage autopay payments': 'manage autopay payments',
    'You can edit the details of your AutoPay Payment Series...':
      'You can edit the details of your AutoPay Payment Series by using the fields below.',
    'Click here to read the AutoPay Terms and Conditions':
      'Click here to read the AutoPay Terms and Conditions.'
  },
  orderReplacementCards: {
    issueNewCard:
      'Please confirm that you would like to issue new cards to the following people:',
    newCardRequested:
      'New cards are being requested because one or more were lost or stolen.',
    reportingCardMissing:
      'If you proceed with reporting these cards as missing, they will be deactivated immediately.',
    notAbleToMakePurchase:
      'You will not be able to make purchases with these cards if found or recovered.',
    lostOrStolen: 'Whose cards were lost or stolen?',
    newCardWilBeSent:
      'New cards will be sent to the business address on file within 10 business days:',
    buyersToOrderCardsfor: 'Buyers to order cards for',
    partialSuccessError:
      'There was an error reactivating some of the reported lost/stolen accounts.',
    completeError:
      'Any requested replacement cards have successfully been sent. However, we were not able to process all account lost/stolen requests.',
    cardReplacementFailed: 'Replacement card request failed.',
    cardDeactivation:
      'The following buyers’ cards have been reported as lost or stolen and have been deactivated.',
    'Please resend requests for the following people:':
      'Please resend requests for the following people:',
    'Please add the following accounts manually:':
      'Please add the following accounts manually:',
    'We’re sorry for the inconvenience. Please...':
      'We’re sorry for the inconvenience. Please manually re-add any affected accounts.',
    'For assistance, please contact Credit Services at':
      'For assistance, please contact Credit Services at'
  },
  creditLineIncrease: {
    'Request a Line of Credit Increase': 'Request a Line of Credit Increase',
    creditIncreaseSuccess:
      'Your credit line increase request was sent successfully. You will receive an email confirmation when your request has been processed.',
    avgProcessDuration:
      'On average, we are able to process credit line increase requests within 5 business days.'
  },
  creditLimitIncreaseAuto: {
    'Request a Credit Limit Change': requestLimitChange,
    'Please provide the information below...':
      'Please provide the information below to start your request. If your request is approved, your credit limit change will take place immediately. If your request requires review, you will receive an email notification with the crediting decision typically within 5 business days.',
    'Information Needed for Request': 'Information Needed for Request',
    'Desired Credit Line': 'Desired Credit Line',
    'Submit Request': 'Submit Request',
    'All fields are required unless marked optional.':
      'All fields are required unless marked optional.',
    optional: 'Optional',
    'Request Approved': 'Request Approved',
    'Request is approved': 'Request is approved.',
    'Request in review': 'Request in review',
    'Request is being reviewed': 'Request is being reviewed.',
    'Congratulations! Your credit limit request has been approved...':
      'Congratulations! Your credit limit request has been approved and is available for immediate use.',
    'Your credit limit request was sent successfully and will be reviewed...':
      'Your credit limit request was sent successfully and will be reviewed. You will receive an email confirmation when your request has been processed.',
    'However, we are unable to grant the full amount you requested':
      'However, we are unable to grant the full amount you requested. You will receive a letter in 7-10 business days explaining the reason for our decision.',
    'New credit limit available for immediate use':
      'New credit limit available for immediate use: ',
    'Your credit limit cannot be increased at this time.':
      'Your credit limit cannot be increased at this time.',
    'You will receive a letter in 7-10 business days explaining the reason for our decision.':
      'You will receive a letter in 7-10 business days explaining the reason for our decision.',
    'Desired Credit line is less than or equal to current credit line.':
      'Desired Credit line is less than or equal to current credit line.',
    'Desired Credit line should be more than $1000 from current credit line.':
      'Desired Credit line should be more than $1000 from current credit line.',
    'Sorry, a request for a credit limit change is not available on this account.':
      'Sorry, a request for a credit limit change is not available on this account.'
  },
  creditLimitIncreaseTaxClassificationOptions: {
    corporation: 'Corporation',
    'Choose one': 'Choose one',
    'Government Department or Agency, Entity established under US/State law...':
      'Government Department or Agency, Entity established under US/State law (e.g. Public School, Public Hospital)',
    'Insurance Company': 'Insurance Company',
    'Limited Liability Company (LLC) including General Partnership':
      'Limited Liability Company (LLC) including General Partnership',
    'Property Management / Property Management, LLC':
      'Property Management / Property Management, LLC',
    'Non-profit Corporation': 'Non-profit Corporation',
    'Personal/Business Trust': 'Personal/Business Trust',
    'Private School': 'Private School',
    'Public Accounting Firm': 'Public Accounting Firm',
    'Publicly Traded Entity or Entity Majority-Owned by a Publicly Traded Entity':
      'Publicly Traded Entity or Entity Majority-Owned by a Publicly Traded Entity',
    'Regulated Financial Institution': 'Regulated Financial Institution',
    'Sole Proprietorship': 'Sole Proprietorship',
    'Unincorporated Association (e.g. Home Owners Association, Girl Scout Troop)':
      'Unincorporated Association (e.g. Home Owners Association, Girl Scout Troop)',
    other: 'Other',
    'Government Department or Agency, Entity established under US/state law...':
      'Government Department or Agency, Entity established under US/state law (e.g. Public school, Public hospital, library, military)'
  },
  invoice: {
    invoices: 'Invoices',
    selected: 'Selected',
    pay: 'Pay',
    selectedInvoices: 'Selected Invoices',
    printRangeOfInvoices: 'Print Range of Invoices',
    'Download Range of Invoices': 'Download Range of Invoices',
    submit: 'Submitted',
    phoneNo: 'Phone No.',
    downloadInvoicesTitle:
      'You can download a range of invoices up to a year old in your choice of file format.',
    'All fields are required.': 'All fields are required.',
    downloadInvoice: 'Which invoices would you like to download?',
    downloadInvoicesStartDate: 'Date must be before the End Date',
    downloadInvoicesEndDate: 'Date must be after the Start Date',
    downloadInvoicesEndDateErrorMessage:
      'End date should not be greater than 90 days from Start Date',
    accountsIncluded: 'Which accounts should be included?',
    dueToVolumeRangeRestriction:
      'Due to volume, we are not able to provide ranges longer than 90 days.',
    'Which invoices would you like to print?':
      'Which invoices would you like to print?',
    timeLapsed: 'Time lapsed',
    'All invoices from the past 90 days': 'All invoices from the past 90 days',
    'A range of invoices within the past 90 days':
      'A range of invoices within the past 90 days',
    'Primary Account': 'Primary Account',
    'Secondary/Job Accounts': 'Secondary/Job Accounts',
    'Start Date is required.': 'Start Date is required.',
    'End Date is required.': 'End Date is required.',
    'Date cannot be more than 365 days in the past.':
      'Date cannot be more than 365 days in the past.',
    'Date cannot be in the future.': 'Date cannot be in the future.',
    'Date cannot be more than 90 days in the past.':
      'Date cannot be more than 90 days in the past.'
  },
  ibp: {
    ibpBenefitsSectionTitle: 'Invoice Net Terms (INT) benefits:',
    termsConditionTitle: 'Invoice Net Terms & Conditions',
    contactInfoText: `For any questions regarding Invoice Net Terms, call ${displayName} Credit Services at `,
    emailUpdatedSuccessfully: 'Email updated successfully.',
    emailWillReceiveCopies:
      '%unMaskEmail% will now receive PDF copies of new invoices by email.',
    parentWithChild: 'parent with child',
    invoiceNetTermsStatus: 'Invoice Net Terms (INT) Status',
    emailsSentTo: 'Alert emails with copies of new invoices are being sent to',
    unEnrolled: 'UnEnrolled',
    enrolled: 'Enrolled',
    ending: 'ending',
    starting: 'starting',
    enrolledOrUnenrolledChildAccounts: '%ibpStatus% Child Accounts',
    childAccountsWithINT: ({ ibpStatus, endingOrStarting, billingDate }) => (
      <Body weight="bold">
        {`${ibpStatus} child accounts with INT ${endingOrStarting} on ${stringToDateFormat(
          billingDate
        )}`}
      </Body>
    ),
    onStatementDate: 'on %nextStatementDate%',
    enrollBackOnNextBillingCycle:
      'Enrollment back into INT is not possible until your next billing cycle %statementDate%.',
    unenrollOnNextBillingCycle:
      'Unenrollment from  INT is not possible until your next billing cycle %statementDate%.',
    beginOnNextCycle:
      'INT functionality for this account will begin with your next billing cycle %statementDate%.',
    endOnNextCycle:
      'INT functionality for this account will end with your next billing cycle %statementDate%.',
    youAreUnEnrolled: (): ReactElement => (
      <>
        You are{' '}
        <Body weight="bold" isInline>
          unenrolled
        </Body>{' '}
        from Invoice Net Terms.
      </>
    ),
    youAreEnrolled: (): ReactElement => (
      <>
        You are{' '}
        <Body weight="bold" isInline>
          enrolled
        </Body>{' '}
        in Invoice Net Terms.
      </>
    ),
    'This account and any selected child accounts have been successfully unenrolled...':
      'This account and any selected child accounts have been successfully unenrolled from INT.',
    'This account has been successfully unenrolled from INT':
      'This account has been successfully unenrolled from INT.',
    'Enroll in Invoice Net Terms (INT)': 'Enroll in Invoice Net Terms (INT)',
    'Moving to invoice billing from monthly statements...': `Moving to invoice billing from monthly statements can facilitate
  timely reconciliation of Invoices with POs and offers more
  flexibility for your internal processes. The ease of receiving PDF
  invoices by email expedites uploading invoices to your ERP or
  sharing them with colleagues. Invoices are sent to a specified email
  address each time new invoices become available.`,
    'Changes to the account will take place after your current billing cycle on %nextStatementDate%.':
      'Changes to the account will take place after your current billing cycle on %nextStatementDate%.',
    'Where should we send your invoices': 'Where should we send your invoices?',
    'Enroll Child Accounts': 'Enroll Child Accounts',
    'Select any child accounts that you want to register...': `Select any child accounts that you want to register in Invoice
    Net Terms. At this time, only child accounts that have enrolled
    in eService and are managed by this account are eligible for
    INT. You can update enrolled child accounts later in the Invoice
    Net Terms settings.`,
    'Agree Terms And Conditions': 'Agree Terms And Conditions',
    'Yes, I want to enroll in Invoice Net Terms...': `Yes, I want to enroll in Invoice Net Terms (INT) and I agree to the
    Invoice Net Terms & Conditions.`,
    'Receive PDF invoices by email within 24 hours...':
      'Receive PDF invoices by email within 24 hours of purchase settlement.',
    'Each invoice will now have Net Terms.':
      'Each invoice will now have Net Terms.',
    'Expedite uploading of invoices to your ERP...':
      'Expedite uploading of invoices to your ERP and facilitate internal distribution.',
    'Your payment terms are indicated on your invoice payment due date.':
      'Your payment terms are indicated on your invoice payment due date.'
  },
  ibpTermsAndConditionsList: {
    'I understand that INT is an invoice billing option and...':
      'I understand that INT is an invoice billing option and I will no longer receive electronic or paper statements.',
    'I understand that I am responsible for paying invoices...':
      'I understand that I am responsible for paying invoices by the due date on each invoice.',
    'I will receive emails with invoices attached...':
      'I will receive emails with invoices attached, with the ability to download them online.'
  },
  gridNoRecords: {
    noInvoices: 'No Invoices',
    noOpenInvoices: 'No Open Invoices',
    noClosedInvoices: 'No Closed Invoices',
    noPaymentsPresent: 'No Payments Present',
    noPaymentsAccountPresent: 'No Payments Account Present',
    noChildAccounts: 'No Child Accounts',
    noBuyersPresent: 'No Buyers Present',
    noActivity: 'No Activity',
    noMessagestoShow: 'No Messages to Show',
    noAccountsLinked: 'No Accounts Linked',
    noTransactionHistory: 'No Transaction History',
    noTransactionDetail: 'No Transaction Detail',
    previousSixMonthsTranscation:
      'Only transactions from the previous 6 months are available to view online.',
    noMatchesFound: 'No matches found.'
  },
  grid: {
    selectAllRecordOption: 'Select all record option',
    sortInAscendingOrder: 'Sort in ascending order',
    sortInDescendingOrder: 'Sort in descending order',
    details: 'details',
    'Enter page number': 'Enter page number',
    'Decrease page': 'Decrease page',
    'Increase page': 'Increase page'
  },
  frequentlyAskedQuestions: {
    title: 'Frequently Asked Questions',
    loginQuestions: 'Login Questions',
    payments: 'Payments',
    invoices: 'Invoices',
    technicalHelp: 'Technical Help',
    interestCharges: 'Interest Charges and Late Fees',
    webPageIssues: 'Web Page Issues',
    contactUs: 'Contact Us',
    updateaccountInfo: 'Update account Information',
    accountActivity: 'Account Activity',
    replacementCards: 'Replacement Cards',
    securityInfo: 'Security Information',
    credit: 'Credit',
    taxExemption: 'Tax Exemption',
    onlineMessageCenter: 'Online Message Center',
    samsClubRewards: `Sam's Club Rewards`
  },
  userGuide: {
    start: 'Getting Started',
    forMoreInformation:
      'For more information on the features and functionality of this site, please view our video tutorials or reference the Frequently Asked Questions section below.',
    allVideos: 'See All videos'
  },
  editJobName: {
    successfullyEdited:
      'The selected PO Numbers / Job Names have been successfully edited.',
    'This invoice’s PO # / Job Name was successfully updated.':
      'This invoice’s PO # / Job Name was successfully updated.',
    header: 'Bulk Edit PO No. / Job Name',
    youCanBulkEdit:
      'You can bulk edit PO No. / Job Name of a selection of invoices by selecting them from the table below. Search by Invoice No. or PO No. by using the fields below. These changes will take effect immediately.',
    selectInvoicesToEdited: 'Select Invoices to be Edited',
    confirmYourIdentity: 'Confirm Your Identity'
  },
  editBusinessInformation: {
    title: 'Edit Business Information',
    contactInfoUpdated:
      'The contact information for your business was successfully updated.',
    businessAddress: 'Business Address',
    'Primary Business Phone': 'Primary Business Phone',
    titleText: 'Is this a mobile phone?',
    declaration:
      'I consent SYNCB and any other owner or servicer of my account to contact me regarding my account using any contact information I have provided. This may include text messages, automatic telephone dialing systems, and / or an artificial or prerecorded voice. This consent applies even if you are charged for the call under your phone plan. You are responsible for any charges that may be billed to you by your communications carriers when we contact you.',
    doYouConsent: 'Do you consent?'
  },
  editBusinessInformationDC: {
    contactHeading:
      'For your account protection, only the Primary Account holder may change the following on the account:',
    contactUsSamsDC: (): ReactElement => (
      <Body>
        Please call Credit Services at{' '}
        <Body isInline weight="bold">
          {closeAccountService}
        </Body>{' '}
        to process this request.
      </Body>
    ),
    services: (): ReactElement => (
      <List type="disc">
        <li>Change of address (City , State, Zip)</li>
        <li>Add/Change of Email Address</li>
        <li>Cell Phone</li>
        <li>Home Phone</li>
        <li>Business Phone</li>
        <li>Name Change</li>
      </List>
    ),
    electronicStatementEmail:
      'Please provide the email address for Electronic Statement delivery. Any email address you submit will replace existing information on your account including where your electronic statement notifications are sent.',
    declaration: `By providing this user's email address, you consent on their behalf to receive email communications regarding their account including special offers and updates.`
  },
  ConfirmDialogConfirmation: {},
  authorizePaymentDetails: {
    'Payment Amount': 'Payment Amount',
    'Recurring Payment Amount': 'Recurring Payment Amount',
    'Pay To': 'Pay To',
    'Pay From': 'Pay From',
    'Payment Date': 'Payment Date',
    'Scheduled Payment Date': 'Scheduled Payment Date'
  },
  authenticationForm: {
    title: 'Account Information'
  },
  accountInformation: {
    'We are unable to identify this account': `We're unable to identify this account. Please review the account number and try again or `,
    'Enter your account number (no spaces).':
      'Enter your account number (no spaces).',
    "For Lowe's Commercial Account (LCA) customers:":
      "For Lowe's Commercial Account (LCA) customers:",
    'Please enter the first 11 digits of your account number.':
      'Please enter the first 11 digits of your account number.',
    "For MyLowe's Pro Rewards Credit Card Customers:":
      "For MyLowe's Pro Rewards Credit Card Customers:",
    'Please enter your 14-digit account number, excluding the first two numbers (79).':
      'Please enter your 14-digit account number, excluding the first two numbers (79).',
    "Please enter your account number (no spaces) found on your welcome letter or Lowe's plastic.":
      "Please enter your account number (no spaces) found on your welcome letter or Lowe's plastic.",
    'register your account': 'register your account',
    'We are having trouble identifying this User ID': `We're having trouble identifying this User ID.`,
    recoverYourUserID: 'recover your User ID.',
    Please: 'Please '
  },
  registration: {
    adminText:
      'An Administrator must have authority to manage financial accounts on behalf of the business. Administrators are able to perform all actions on this account, including managing non-administrator users.',
    adminInfo: 'Administrator Information',
    confirmInfo: 'Confirm Your Contact Information',
    adminAccessText:
      'The following contact information will be associated with your administrator access for this account. If possible, provide a phone number and email address that only you can access.',
    userIdPassword: 'Create a User ID and Password',
    createNewPassword: 'Create a New Password',
    mothersDOB: 'Month and day of your mother’s birthday:',
    consentVerbiageWithAuthCode: `By clicking COMPLETE REGISTRATION below to register your account for ${displayName} Business Credit Center Online Servicing, you are also agreeing to receive electronic communications for all your ${displayName} Synchrony accounts pursuant to our updated`,
    consentVerbiageWithoutAuthCode: `By clicking CONTINUE TO ACCOUNT below to register your account for ${displayName} Business Credit Center Online Servicing, you are also agreeing to receive electronic communications for all your ${displayName} Synchrony accounts pursuant to our updated`,
    useNewCredentials:
      'You can now use the User ID and Password you just entered to log in to your eService account any time.',
    credentialsGeneratedForYou:
      'A User ID and password are being generated for you and will arrive at the address on file in 7–10 business days.',
    confirmationText:
      'Once you have received your User ID and temporary password in the mail, follow the instructions on the letter to complete your registration.',
    samsAuthorityRequired:
      'To register, you must have the authority to manage this Sams Club credit account.',
    authenticationCodeRequired:
      'An authentication code is required for registration and can be found in your welcome letter or on your most recent billing statement. Additional details provided below.',
    finishSettingAccount: 'Finish Setting Up Your Admin Account',
    invalidInfo: 'Invalid Account Information',
    invalidInfoEntered:
      'Invalid account information has been entered. Please re-enter your information and try again.',
    unableRegister:
      'If you are still unable to register, please contact us at:',
    registrationComplete: 'Registration complete!',
    preRegistrationComplete: 'Pre-registration complete.',
    'Congratulations, your account has been registered for online access!':
      'Congratulations, your account has been registered for online access!',
    'As the online account administrator, you are able to control...':
      'As the online account administrator, you are able to control what functions a Child account has access to online. Would you like to provide online access to your Child accounts and set their usage permissions now?',
    authentication: 'Authentication'
  },
  saveUserRegistrationPrivileges: {
    'Set Child Account Privileges': 'Set Child Account Privileges',
    'Send Login Credentials to Child Accounts':
      'Send Login Credentials to Child Accounts',
    'Child accounts have been successfully signed up...':
      ' Congratulations, your Child accounts have been successfully signed up with the privileges you specified.'
  },
  increaseCreditline: {
    currentCreditLine: 'Current Credit Line:',
    outstandingBalance: 'Outstanding Balance:',
    desiredCreditLine: 'Desired Credit Line',
    enterDollarAmountNumbers:
      'Desired Credit Line. Enter whole dollar amounts using numbers only',
    submitRequest: 'Submit Request'
  },
  requestPaperStatement: {
    fillInTheInformationBelow:
      'To receive a copy of a statement, please fill in the information below.',
    youWillReceiveConsolidatedStatement:
      'If this account manages secondary/job accounts, you will receive a consolidated statement including all managed accounts.',
    inMostCases:
      'In most cases, we are able to process requests for copies of billing statements within 3 business days. Our system allows us to print statements that are 12 months old or newer.',
    yourStatementCopyWillBeMailed:
      'Your statement copy will be mailed to the following company address:',
    enterRecipientFullName: 'Enter recipient full name',
    'No Statements to Show': 'No Statements to Show',
    'Only statements processed after enrollment can be displayed...':
      'Only statements processed after enrollment can be displayed online at this time.',
    'If you need to review an earlier statement, you can':
      'If you need to review an earlier statement, you can',
    'Request a Paper Statement': 'Request a Paper Statement',
    'to have a hard copy mailed to you.': 'to have a hard copy mailed to you.'
  },
  closeAccount: {
    title: 'Close Account',
    closed: 'Your account has been closed.',
    confirmCloseTitle: 'Confirm Close Account',
    closedSucessfully: 'This account has been closed successfully.',
    willNoLongerPurchase:
      'If you close your account, you will no longer be able to make purchases.',
    confirmCloseMessage:
      'Are you sure you want to close your account? If you close your account, you will no longer be able to make purchases.',
    canNoLongerPurchase: 'You can no longer make purchases with this account.',
    balanceMessage:
      'You are still responsible for paying any open statements. Access to your online account will remain available until balance is paid off.'
  },
  login: {
    welcome: 'Welcome to',
    "Manage your Lowe's Commercial and MyLowe's Pro Rewards Credit Card account": `Manage your Lowe's Commercial and MyLowe's Pro Rewards Credit Card account online from any device anytime, anywhere. Easily and securely access your invoices, statements, pay your bill and more.`,
    'Manage your %displayName% Business Mastercard, %displayName% Business Credit Card and %displayName% Direct Account': `Manage your %displayName% Business Mastercard, %displayName% Business Credit Card and %displayName% Direct Account. Easily and securely access your invoices and statements, pay your bill and more.`,
    'Manage your %displayName% Premier Credit Card account easily and securely. Access your invoices, statements, pay your bill and more': `Manage your %displayName% Premier Credit Card account easily and securely. Access your invoices, statements, pay your bill and more.`,
    'Manage your %displayName% Commercial Credit Card account online from any device anytime, anywhere': `Manage your %displayName% Commercial Credit Card account online from any device anytime, anywhere. Easily and securely access your invoices, statements, pay your bill and more.`,
    FloorAndDecorAuthorityRequired: `To register, you must have the authority to manage this ${displayName} Premier credit account.`,
    newAccount: 'New account?',
    setUpOnlineAccess: 'Set up online access',
    setUpOnlineAccessMessage:
      'to pay your bill and manage your account online.',

    // Account Help Tooltip Strings
    'Not the right account type?': 'Not the right account type?',
    'Look up your account': 'Look up your account',
    'Your account number can be found on your statement and begins with':
      'Your account number can be found on your statement and begins with',

    recoverYourUserID: 'Recover your User ID',
    resetYourPassword: 'Reset your Password',
    rememberMe: 'Remember Me',
    logIn: 'Log In',
    unRecognizedUserIdAndPassword:
      "We don't recognize this User ID and Password combination.",
    loginAlert:
      'To keep your account secure, multiple failed attempts will result in locking this User ID. A locked User ID can only be unlocked by your Account Administrator or our help desk.',
    unRecognizedUserIDAndPasswordMessage:
      "We don't recognize this User ID and Password combination.",
    userIdLockedAlertMessage:
      'You have 1 login attempt remaining before your User ID is locked.',
    unLockUserId:
      'A locked User ID can only be unlocked by your Account Administrator or our help desk.',
    accountLocked: 'Account Locked',
    incorrectLoginAttempts:
      'Your account has been locked because there were too many incorrect login attempts.',
    stepToUnlockUserId:
      "Please contact your company's Online Account Administrator and ask them to unlock your User ID.",
    resetPassword:
      'Administrators can restore user access under Account Management > Manage Users. Expand the locked out user and select “Reset Password”.',
    invalidInformation: 'You have provided invalid information.',
    protectionAccountLocked:
      'For your protection your account has been locked.',
    contactCustomerService: (): ReactElement => (
      <>
        Contact Customer Service at{' '}
        {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)} and
        tell the representative you have experienced Registration Error Message
        814. Please have a copy of your most recent billing statement available
      </>
    ),
    invalidAuthCode:
      'You have provided an invalid Auth Code during registration. For your protection, your account has been locked.',
    contact:
      'Please contact Customer Service for your applicable account, and have a copy of your most recent billing statement available.',
    problemWithFormSubmitAgain:
      'There are problems with your form. Please make corrections and Submit again.',
    accountTypeNotSupported: 'Account type not supported!',
    click: 'Click',
    returnBackToLegacyUrl: 'to log back to old site.',
    samsAccountNotSupported:
      'Our technical team is currently working on resolving the issue. We apologize for any inconvenience. Please click',
    toLogin: 'to login.',
    'Online Access for this User ID is currently unavailable':
      'Online Access for this User ID is currently unavailable.',
    administratorLockedOut: `If you're an Administrator and locked out, please contact us at`
  },
  manageLinkedAccounts: {
    'The account': 'The account',
    'was linked successfully': 'was linked successfully.',
    'Manage Linked Accounts': 'Manage Linked Accounts',
    'Link Account': 'Link Account'
  },
  IEBanner: {
    IEBannertitle: 'Internet Explorer support discontinued.',
    IEBannerContent: (): ReactElement => (
      <>
        {storeName} is no longer accessible on Internet Explorer. To continue
        servicing your account, please use{' '}
        <a
          href="https://www.google.com/chrome"
          target="_blank"
          rel="noopener noreferrer"
        >
          Chrome
        </a>
        ,{' '}
        <a
          href="https://www.mozilla.org/en-US/firefox/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Firefox
        </a>{' '}
        and/or{' '}
        <a
          href="https://www.apple.com/safari/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Safari
        </a>{' '}
        browsers.
      </>
    )
  },
  coBrowseModal: {
    'Initiating Co-Browse session': 'Initiating Co-Browse session',
    'If you start':
      'If you start this co-browse session, you will be directed to the login screen and your work will not be saved.',
    'Would you like to proceed?': 'Would you like to proceed?',
    'Proceed with co-browse session': 'Proceed with co-browse session'
  },
  footer: {
    onlinePrivacyPolicy: 'Online Privacy Policy',
    websiteUsageAgreement: 'Website Usage Agreement',
    onlineAccessibility: 'Accessibility',
    startCobrowseSession: 'Start a Co browse session',
    cardInformation: `The Sam's Club® MasterCard® is issued by Synchrony Bank pursuant to a license by Mastercard International Incorporated. Mastercard is a registered trademark, and the circles design is a trademark of Mastercard International Incorporated.`,
    secureButton: ({
      onClickHandler,
      secureSiteFocusableRef
    }: {
      onClickHandler: () => void
      secureSiteFocusableRef: React.RefObject<HTMLButtonElement>
    }): ReactElement => (
      <>
        This is a{' '}
        <SecureLink
          id="secureModal"
          buttonType="text"
          onClick={onClickHandler}
          ref={secureSiteFocusableRef}
        >
          secure
        </SecureLink>{' '}
        site.
      </>
    )
  },
  managePaymentAccounts: {
    deletePaymentAccountError:
      "This Payment Account is currently assigned to one or more scheduled payments and can't be deleted at this time. Please edit your scheduled payments or wait until the payment clears to delete this account.",
    deletePaymentAccountConfirmation: (): ReactElement => (
      <>
        Are you sure you want to delete <b>Personal Checking?</b>
      </>
    ),
    'was successfully removed from your payment methods':
      'was successfully removed from your payment methods.',
    'was successfully added as a payment method':
      'was successfully added as a payment method.',
    'Our system indicates a payment associated with this bank...':
      'Our system indicates a payment associated with this bank. Please try deleting bank account later.',
    'Go to add payment account': 'Go to add payment account'
  },
  lostStolenCard: {
    title: 'Report Card as Lost or Stolen',
    stoleCardDescription: (): ReactElement => (
      <>
        <b>
          If you report this card as lost/stolen, the account will be closed
          immediately.{' '}
        </b>
        You will not be able to make new purchases and the account will no
        longer be accessible online.
      </>
    ),
    newCardDescription:
      'You will be issued a new account number and your new card will be mailed to the business address on file within 10 business days. When you receive your new card and authentication code, you will need to register your new account for online access.'
  },
  oneTimePayment: {
    invalidPaymentAmountTitle: 'Invalid Payment Amount',
    invalidPaymentAmountMessage:
      'Our system indicates an invalid payment amount provided, please submit a payment again with valid amount up to two decimal values.',
    paymentAmountExceedsMessage:
      'Our system indicates the payment amount exceeds current balance. For assistance, please call:',
    paymentAmountExceedsPhone: `${creditServiceNumber}`,
    accountDeliquentTitle: 'Account Delinquency Exceeded',
    accountDeliquentMessageLineOne:
      'Our system indicates account is delinquent, you may reach out to customer care in help center.',
    paymentFailedTitle: 'Technical Issue',
    paymentFailedMessage: (): ReactElement => (
      <>
        We are unable to process payments at this point of time, For further
        assistance- you may reach out to customer care in{' '}
        <Link isInline to={routePaths.userGuide}>
          help center
        </Link>
      </>
    ),
    bankIdMismatchTitle: 'Payment Method Corrupted',
    bankIdMismatchMessage:
      'Our system indicates an invalid payment method selected, You may delete payment method and re-add it in Manage Payment Methods.',
    scheduledPaymentExistsMessage:
      'Our system indicates that you have already scheduled a payment for today. For security reasons, you may only schedule one payment per day at this time. You may cancel or reschedule the payment you have already scheduled today in Payment History.',
    paymentMethodUnauthorizedMessage:
      'Our system indicates that the bank has rejected payment.',
    invalidPaymentDateTitle: 'Invalid Payment Date',
    invalidPaymentDateMessage: `Our system indicates you are trying to schedule payment beyond today's cut off timing, please select any date other than today.`,
    paymentDateVerbiage:
      'A payment made by 11:59 PM EST will be credited as a same day payment. A payment made after this time will be credited on the next business day.'
  },
  addNewPaymentAccount: {
    title: 'Add New Payment Account',
    addAccount: 'Add Account',
    fieldNameIsRequired: '%fieldName% is Required.',
    'Routing number must be 9 digits': 'Routing number must be 9 digits',
    'Account number must be 5-16 digits': 'Account number must be 5-16 digits',
    canFindInformation:
      'You can find routing and account information for most checking accounts on the bottom of your checks. Routing numbers are commonly found on you bank’s website.',
    authorizeSynchronyBank:
      'I authorize Synchrony Bank to initiate electronic payment when I submit online payment requests using the bank account indicated above, and I authorize my bank to honor the withdrawals. This authorization pertains to the loan account noted above and is to remain in effect until it is canceled by Synchrony Bank, my financial institution, or me.',
    noFundsTransfer:
      'There will be no funds transfer at this time. The authorization of your bank to transfer funds to your loan account occurs only when you complete a payment request.'
  },
  clientOffering: {
    'Log in to get started': 'Log in to get started',
    'Manage your Account online': 'Manage your Account online:',
    'Account overview': 'Account overview',
    'Electronic Statements': 'Electronic Statements',
    Payments: 'Payments',
    'Download transaction details': 'Download transaction details',
    'View your Account History and more': 'View your Account History and more'
  },
  button: {
    cancel: 'Cancel',
    cancelAutopay: 'Cancel Autopay',
    closeAccount: 'Close Account',
    close: 'Close',
    goToNext: 'Go to Next',
    next: 'Next',
    printConfirmation: 'Print Confirmation',
    returnToAccountOverview: 'Return to Account Overview',
    'Return to Account Summary': 'Return to Account Summary',
    'Report & Close Account': 'Report & Close Account',
    update: 'Update',
    print: 'print',
    logInAgain: 'Log In Again',
    continueSession: 'Continue Session',
    deactivateUser: 'Deactivate User',
    resetPassword: 'Reset Password',
    saveChanges: 'Save Changes',
    payment: 'Payment',
    view: 'View',
    backToTop: 'Go Back to Top',
    submit: 'Submit',
    goBack: 'Go Back',
    returnToLogInScreen: 'Return to log in screen',
    continueToAccount: 'Continue to account',
    download: 'Download',
    switchAccount: 'Switch Account',
    reply: 'Reply',
    send: 'Send',
    printThisPage: 'Print this page',
    cancelAutoPaySeries: 'Cancel AutoPay Series',
    'Cancel Autopay Payment': 'Cancel Autopay Payment',
    downloadTransactions: 'download transactions',
    logout: 'Logout',
    addUser: 'Add User',
    'Make Payment': 'Make Payment',
    'Start Autopay': 'Start Autopay',
    'Schedule One-Time Payment': 'Schedule One-Time Payment',
    enroll: 'Enroll',
    UNENROLL: 'UNENROLL',
    'Return to Messages': 'Return to Messages',
    'Add Account': 'Add Account',
    'Save Privilege Settings': 'Save Privilege Settings',
    'Back to Account Summary': 'Back to Account Summary',
    'Set up Child Accounts': 'Set up Child Accounts',
    'UPDATE PRIVILEGES': 'UPDATE PRIVILEGES',
    'Return to Manage Buyers': 'Return to Manage Buyers',
    'Go to Account': 'Go to Account',
    'Return to Login Screen': 'Return to Login Screen'
  },
  link: {
    manage: 'Manage',
    recoverUserIdLink: 'recover it here',
    findMyAuthCode: 'Where can I find my authentication code?',
    accountPermissionInfoLink: 'Learn more about account permissions',
    consentToECom: 'Consent to Electronic Communications.',
    here: 'here',
    'learn more': 'learn more'
  },
  label: {
    accountNumber: 'Account Number',
    accounts: 'Accounts',
    amountToPay: 'Amount to Pay',
    authCode: 'Authentication Code',
    businessZipCode: 'Business Zip Code',
    buyer: 'Buyer',
    choosePaymentAmount: 'Choose payment amount',
    email: 'Email',
    enterConfirmationEmail: 'Enter confirmation email',
    enterPassword: 'Enter password',
    enterUserId: 'Enter User ID',
    from: 'From',
    otherAmount: 'Other Amount',
    password: 'Password',
    payFrom: 'Pay From',
    payTo: 'Pay To',
    payDate: 'Pay Date',
    paymentAmount: 'Payment Amount',
    paymentDate: 'Payment Date',
    'Email Confirmation': 'Email Confirmation',
    phoneNumberAriaLabel: 'Phone number. Enter numbers only',
    'Work Phone number. Enter numbers only':
      'Work Phone number. Enter numbers only',
    selectFromDay: 'select from day',
    selectFromMonth: 'select from month',
    selectFromYear: 'select from year',
    'select payment account': 'select payment account',
    'Payment Account': 'Payment Account',
    'select payment date': 'select payment date',
    selectToMonth: 'select to month',
    selectToYear: 'select to year',
    'Select terms and conditions': 'Select terms and conditions',
    sendACard: 'Send a card',
    submitted: 'Submitted',
    switchAccountType: 'Switch account type',
    to: 'To',
    userId: 'User ID',
    selectColumnToFilter: 'Select column to filter',
    confirmationReceiptSentTo: 'A confirmation receipt has been sent to',
    'New Email': 'New Email',
    'ending in': 'ending in'
  },
  modalsTitle: {
    lostStolenCard: 'Confirm Lost or Stolen Report',
    secureSite: 'This is a secure site.',
    selectedAccounts: 'Selected Account(s)',
    requestPaperStatement: 'Request a Paper Statement',
    showAuthenticationCode: 'Where can I find my code?',
    userResetPassword: 'Reset Password',
    userdeactivate: 'Deactivate User',
    'Change eStatement Email': 'Change eStatement Email',
    'Manage Payment Accounts': 'Manage Payment Accounts'
  },
  modalContent: {
    wantToReportCard:
      'Are you sure you want to report a card as lost or stolen? The account will be closed immediately.',
    wontBeAbleToAccessAccount:
      'You will not be able to access your account online until you receive a new card and register the new account for online access.',
    closeModal: 'click to close the modal',
    'You can update the email that receives eStatements for this account below':
      'You can update the email that receives eStatements for this account below.',
    'Your request to update the email...':
      'Your request to update the email that receives e-Statements was submitted successfully.',
    'Email address updated': 'Email address updated.'
  },
  resetPassword: {
    passwordSuccess: 'Your Password has been successfully updated.',
    notReuseExistingPassword: `To keep this account secure, you may not reuse any of your previous 10
    passwords for this user. Please come up with a new password.`,
    'We don’t recognize this Password. Please enter your current password':
      'We don’t recognize this Password. Please enter your current password.',
    enterNewPassword: 'Please enter a new password.',
    newPasswordMust: 'Your new password must:',
    charactersInLength: 'be at least 7 characters in length (max 15).',
    includeUpperCase: 'include at least 1 uppercase letter.',
    includeLowerCase: 'include at least 1 lowercase letter.',
    includeNumbers: 'include at least 2 numbers.',
    specialCharacters: 'Special characters are permitted.',
    lastTenPasswordsCannotBeUsed:
      'Special characters are permitted. You cannot use any of the last 10 passwords used for this account.',
    'Please provide the following information in order to reset your password':
      'Please provide the following information in order to reset your password.',
    'Our system has detected this device may not be secure. Try again from another device, preferably one that only you can access':
      'Our system has detected this device may not be secure. Try again from another device, preferably one that only you can access.'
  },
  transmitErrorMessages: {
    'We apologize. We are currently experiencing technical difficulties and are unable to access your account. Please try again later.':
      'We apologize. We are currently experiencing technical difficulties and are unable to access your account. Please try again later.',
    'We apologize. We’re experiencing technical difficulties. For assistance, please call us at':
      'We apologize. We’re experiencing technical difficulties. For assistance, please call us at:',
    'The verification process was cancelled. If the cancellation was an error, please try again.':
      'The verification process was cancelled. If the cancellation was an error, please try again.',
    'You’ve reached the maximum number of attempts. To receive a new code, please try again.':
      'You’ve reached the maximum number of attempts. To receive a new code, please try again.',
    'Your session has timed out. Please try again.':
      'Your session has timed out. Please try again.'
  },
  placeHolderValues: {
    authenticationCode: 'Authentication Code',
    accountNumber: 'Account Number',
    businessZipCode: 'Business Zip Code',
    userID: 'User ID',
    currentUserID: 'Current User ID',
    newUserID: 'New User ID',
    re_enterNewUserID: 'Re-enter New User ID',
    chooseFileFormat: 'Choose File Format',
    startDate: 'Start Date',
    endDate: 'End Date',
    sendConfirmationEmailTo: 'Send Confirmation Email to',
    'Work Email': 'Work Email',
    'Work Phone': 'Work Phone',
    'Enter Work Email': 'Enter Work Email',
    'Mobile Phone Number': 'Mobile Phone Number',
    'Enter Mobile Phone Number': 'Enter Mobile Phone Number',
    yourMessage: 'Your message',
    streetAddress: 'Street Address',
    streetAddressline2: 'Street Address Line 2',
    city: 'City',
    state: 'State',
    stateAndTerritory: 'State/Territory',
    selectState: 'select state',
    selectStateOrTerritory: 'select state or territory',
    zipCode: 'ZIP Code',
    businessEmail: 'Business Email',
    'Primary Business Phone': 'Primary Business Phone',
    primaryBusinessEmail: 'Primary Business Email',
    payFrom: 'Pay From',
    amount: 'Amount',
    scheduledPaymentDate: 'Scheduled Payment Date',
    close: 'CLOSE',
    saveChanges: 'Save Changes',
    firstName: 'First Name',
    firstNameFull: 'Full First Name',
    lastName: 'Last Name',
    businessPhone: 'Business Phone',
    newPassword: 'New Password',
    confirmNewPassword: 'Confirm New Password',
    confirmReenterNewPassword: 'Re-Enter New Password',
    currentPassword: 'Current Password',
    year: 'Year',
    month: 'Month',
    day: 'Day',
    phoneNumber: 'Phone Number',
    emailAddress: 'Email Address',
    'Re-enter Email Address': 'Re-enter Email Address',
    recipientFullName: 'Recipient Full Name',
    'Receiving Email': 'Receiving Email',
    yourEmailAddress: 'Your email address',
    enterEmailAddress: 'Enter email address',
    'User ID Recovered': 'User ID Recovered',
    'Work Phone Number': 'Work Phone Number',
    'Routing Number': 'Routing Number',
    'Account Nickname': 'Account Nickname',
    'Business Revenue': 'Business Revenue',
    'Federal Tax Classification': 'Federal Tax Classification'
  },
  errors: {
    applicationDefaultErrorMessage: ({
      phoneNumber
    }: {
      phoneNumber: string
    }): ReactElement => (
      <>
        We are sorry, we have technical difficulties in processing your request,
        please contact {clientName}’s credit services at {phoneNumber} to assist
        you further
      </>
    ),
    title: 'System Error',
    tryAgain:
      'We are unable to perform the requested operation. Please go back and try again.',
    errorPersist:
      'If the error persists or if you are unable to use the platform, please contact us:',
    'Email address is required.': 'Email address is required.',
    'Email is required.': 'Email is required.',
    'Invalid email address.': 'Invalid email address.',
    'Invalid Email.': 'Invalid Email.',
    'Phone number is required.': 'Phone number is required.',
    'Phone number must be 10 digits.': 'Phone number must be 10 digits.',
    'Invalid First Name': 'Invalid First Name.',
    'First Name is required.': 'First Name is required.',
    'Invalid Last Name': 'Invalid Last Name.',
    'Last Name is required.': 'Last Name is required.',
    'Payment Amount is required.': 'Payment Amount is required.',
    'Pay From is required.': 'Pay From is required.',
    'Payment Date is required.': 'Payment Date is required.'
  },
  customerCare: {
    lcaCustomersContact: `LCA customers: ${contactUsProx}`,
    lbaCustomersAccountLock: `MyLowe's Pro Rewards Credit Card Customers: ${contactUsLba}`,
    masterCardCustomersContact: `Business Mastercard: ${contactUsBrcDc}`,
    privateLabelCustomersContact: `Business Credit Card: ${contactUsBrcPlcc}`,
    businessCustomersContact: `Direct Credit Accounts: ${contactUsProx}`,
    directCustomer: `Sam's Club Direct Customers: ${contactUsDirectCustomer}`,
    samsBusinessCenter: `Sam's Club Business Credit`,
    systemErrorContacts: () => (
      <>
        {systemErrorPageContacts.en.map(contact => (
          <CLBody isInline key={contact}>
            {contact}
          </CLBody>
        ))}
      </>
    )
  },
  linkedAccounts: {
    addNewLinkedAccount: 'Add New Linked Account',
    toLinkAccountLogin:
      'To link your account with another registered account, please login to that account below.',
    onceLinkedCannotUnlink: 'Once an account is linked, it cannot be unlinked.'
  },
  secureMessage: {
    sendSuccess: 'Your message was sent successfully.',
    selectMessage: 'Select a message.',
    selectedMessageInfo:
      'Please select a message in the left column to view and reply.',
    'Your message was sent successfully!':
      'Your message was sent successfully!',
    'Secure Messages': 'Secure Messages',
    userDetail: ({
      userEmail,
      date,
      isReply
    }: {
      userEmail: string
      date: string
      isReply: boolean
    }): ReactElement => (
      <>
        On {date},
        <Body weight="bold" isInline>
          {' '}
          {isReply ? userEmail : 'You'}{' '}
        </Body>
        &lt;{userEmail}&gt;
        {isReply
          ? ` from ${displayName} Customer
                Experience Team wrote:`
          : ' wrote'}
      </>
    ),

    brcLabelPrefix:
      secureMessageBrandPrefixes?.brcLabel ||
      secureMessageBrandPrefixes.brcValue,
    brcValuePrefix: secureMessageBrandPrefixes.brcValue,
    dualcardLabelPrefix:
      secureMessageBrandPrefixes?.dcLabel || secureMessageBrandPrefixes.dcValue,
    dualcardValuePrefix: secureMessageBrandPrefixes.dcValue,
    proxLabelPrefix:
      secureMessageBrandPrefixes?.proxLabel ||
      secureMessageBrandPrefixes.proxValue,
    proxValuePrefix: secureMessageBrandPrefixes.proxValue,

    /**
     * These subjects are required for the Secure Message API (moxie team) to
     * function correctly. They are appended to the brand's prefix in the
     * subject line of a message.
     *
     * In the future, it may be necessary to split into label/value as moxie may
     * wish to use a generic value while the user would be displayed a
     * translation in the label.
     */
    'Business Account Information': 'Business Account Information',
    'Business Account Other Inquiries': 'Business Account Other Inquiries',
    subjectAccountInfo: 'Account Information',
    subjectAccountMaintenance: 'Account Maintenance',
    subjectBillingInquiries: 'Billing Inquiries',
    subjectOtherInquiries: 'Other Inquiries',
    subjectPurchaseDisputes: 'Purchase Disputes',
    subjectTechnicalSupport: 'Technical Support'
  },
  menuName: {
    summary: 'Summary',
    makePayment: 'Make a Payment',
    schedulePayment: 'Schedule a Payment',
    viewPayment: 'View Payments',
    paymentHistory: 'Payment History',
    billing: 'Billing',
    statements: 'Statements',
    credits: 'Credits',
    transactions: 'Transactions',
    eStatements: 'eStatements',
    accountMgmt: 'Account Management',
    editUserInfo: 'Edit User Information',
    manageUser: 'Manage Users',
    manageBuyers: 'Manage Buyers',
    managePayment: 'Manage Payment Accounts',
    manageLinkedAccounts: 'Manage Linked Accounts',
    requestBuyerCards: 'Request Cards for Additional Buyers',
    'Report Lost or Stolen': 'Report Lost or Stolen',
    manageChildAccount: 'Child Account Administration',
    invoiceBillPay: 'Enroll in Invoice Net Terms',
    ibpStatus: 'Check INT Status',
    viewStatement: 'View Statements',
    creditLineIncrease: 'Request Credit Limit Change',
    taxforms: 'Download Tax Forms',
    editJobName: 'Batch Edit Job Names',
    logout: 'Log Out',
    helpCenter: 'Help Center',
    userGuide: 'User Guide'
  },
  downloadTransactions: {
    title: 'Download Transactions',
    successfullyDownloaded:
      'The transactions that were selected were successfully downloaded.',
    helpInfo:
      ' You can download a range of transactions from the past 3 months of billing periods below in your choice of file format.',
    billingPeriod: 'Which billing period would you like to download?'
  },
  faq: {
    personalInfoQuestion: 'Why do I need to enter personal information?',
    personalInfoAnswer:
      'We require you to provide us personal information to login to ensure that your account is protected from unauthorized access.',
    infoSecureQuestion: 'Is the information that I enter secure?',
    infoSecureAnswer:
      'Yes, we use SSL encryption, an industry standard, to help protect your account information from unauthorized access. When your browser is in secure mode, you will notice an unbroken key or lock at the bottom of the page.',
    paymentPostedQuestion:
      'What should I do if a payment I sent has not been posted?',
    paymentMethodQuestion: 'What methods of payment can I use to pay my bill?',
    paymentMethodAnswer:
      'We accept checks drawn on US financial institutions, money orders, or electronic payments through online bill services. Gift certificates are not accepted as a form of payment and debit card payments can be made in store only.',
    sendPaymentQuestion: 'Where do I send my payments?',
    sendPaymentAnswer: 'You can send your payments to the following address:',
    sendPaymentAddress: (): ReactElement => (
      <AddressWrapper>
        <p>{displayName}</p>
        <p>PO BOX {payment.postalCode}</p>
        <p>
          {payment.city}, {payment.state} {payment.streetNumber}
        </p>
        <Text>
          <p>
            <strong>Make checks payable to: </strong>
          </p>
          <p>
            <strong>{displayName} Business Account/SYNCB</strong>
          </p>
        </Text>
      </AddressWrapper>
    ),
    paymentStoreQuestion: `Can I make a payments at a ${displayName} store?`,
    paymentStoreAnswer: `Yes you can make a payment for your ${displayName} credit account at any ${displayName} store.`,
    paymentDueDateQuestion: 'Can I get my payment due date changed?',
    paymentDueDateAnswer: (): ReactElement => (
      <Text>
        Yes- we have two payment due dates available. Either the 15th or the
        20th of the month. However this option is not available online, please
        call customer service at{' '}
        {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)} for
        assistance.
      </Text>
    ),
    paymentErrorQuestion: 'The Payment posted on my account was in error.',
    paymentErrorAnswer:
      'If your statement shows that your payment was received, but it is for a different amount than you sent, please accept our apology for any inconvenience caused by this matter. In order to correct any difference in the amount that should have been credited, please send bank verification to the following address',
    paymentErrorVerification:
      'The verification should show the difference charged to your account.Upon receipt of this information, we will give this matter our immediate attention.',
    paymentPostedAnswer2: (): ReactElement => (
      <Text>
        If your payment has not been posted please check with your bank to see
        if the check has been cashed. If it has, please contact Customer Service
        at {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)}
      </Text>
    ),
    paymentMethodAnswer2:
      'You can make a One-Time Payment or schedule AutoPay on the Online Credit Center. We accept checks drawn on U.S. financial institutions, money orders, or electronic payments through online bill services. Gift certificates are not accepted as a form of payment and debit card payments can be made in-store only.',
    sendPaymentAddress2: (): ReactElement => (
      <AddressWrapper>
        <p>{displayName} Business Account</p>
        <p>PO BOX {businessPayments.postalCode}</p>
        <p>
          {businessPayments.city}, {businessPayments.state}{' '}
          {businessPayments.streetNumber}
        </p>
        <Text>
          <p>
            <strong>Make checks payable to: </strong>
          </p>
          <p>
            <strong>{displayName} Business Account/SYNCB</strong>
          </p>
        </Text>
      </AddressWrapper>
    ),
    paymentDueDateAnswer2: `MyLowe's Pro Rewards Credit Card accounts have two payment due dates
        available. Either the 12th or the 28th of the month. However this
        option is not available on line, please call customer service at
        ${creditServiceNumber} for assistance.`,
    paymentIncorrectQuestion:
      'Payment posted on my account was for an incorrect amount?',
    paymentIncorrectAnswer:
      'If your statement shows that your payment was received, but it is for a different amount than you sent, please accept our apology for any inconvenience caused by this matter. In order to correct any difference in the amount that should have been credited; please send bank verification to the following address:',
    'Payments Research': 'Payments Research',
    'General Correspondence': 'General Correspondence',
    'If the amount posted to your account was LESS THAN the amount the check was written for please pay the difference':
      'If the amount posted to your account was LESS THAN the amount the check was written for please pay the difference.',
    'If the amount posted to your account was MORE THAN the amount the check was written for, please contact your bank to have the error corrected':
      'If the amount posted to your account was MORE THAN the amount the check was written for, please contact your bank to have the error corrected.',
    'Please follow the instructions below':
      'Please follow the instructions below.',
    paymentAutoPayQuestion: 'What is AutoPay?',
    paymentAutoPayAnswer:
      'AutoPay withdraws the payment amount you specify every month from your designated payment account on your payment due date. The payment amounts you can choose from are: statement balance, minimum payment or another amount. You can set up AutoPay by selecting “Schedule a Payment” under the “Payments” section',
    paymentPostedAnswer3: `You can notify us online of a payment that has not been credited. Click on the Account Management icon at the top of the page, then within the Report an Issue box on the right side of the page, click on My Payment Isn't Appearing On My Account.`,
    paymentMethodAnswer3:
      'We accept checks drawn on US financial institutions, money orders, electronic payments through online bill services, phone payments (check or debit card) and in-store payments. Gift certificates and other credit cards are not accepted as a form of payment.',
    'We accept checks drawn on US financial institutions, money orders...':
      'We accept checks drawn on US financial institutions, money orders, electronic payments through online bill services, phone payments (check or debit card). Gift certificates, in-store payments and other credit cards are not accepted as a form of payment.',
    onlinePaymentQuestion: 'How can I make a payment online?',
    onlinePaymentAnswer:
      'Click the Make a Payment link and schedule your payment by entering your bank account information and select the date you want the payment to take place.',
    paymentOnlineQuestion:
      'What information will I need to make a payment online?',
    paymentOnlineAnswer:
      'You need to have a valid checking account from a U.S. financial institution and the following information:',
    paymentOnlineList: (): ReactElement => (
      <List type="disc">
        <li>Bank name</li>
        <li>Checking account number</li>
        <li>9 Digit Bank routing number</li>
      </List>
    ),
    paymentRoutingQuestion:
      'Where can I find my checking account number and bank routing number?',
    paymentRoutingAnswer:
      'You can find this information on the bottom of your check.',
    paymentDurationQuestion:
      'How long does it take for a payment online to post to my account?',
    paymentDurationAnswer:
      'The payment will post to your account on the scheduled payment date. It can take up to 2 days to reflect on your account summary.',
    paymentRecurringQuestion:
      'Can I schedule a recurring (automatic) payment online?',
    paymentRecurringAllowed:
      'Yes. You can set up recurring, automatic payments online.',
    paymentEnrollmentQuestion:
      'Can I make a payment online immediately after enrolling?',
    paymentEnrollmentAnswer:
      'Yes. You can make a payment provided it is not greater than your current balance.',
    paymentStatusQuestion: 'How can I find the status of my online payment?',
    paymentStatusAnswer:
      'To find the status of your online payment, click Account Management and then click View and Update in-process payments.',
    stopPaymentQuestion: 'Can I place a stop payment on an online payment?',
    stopPaymentAnswer:
      'Yes, but only if your online payment is pending or has not been processed. To cancel your scheduled online payment, click Account Management and then click View and Update in-process payments.',
    paymentHelpQuestion: 'Can I Speak with someone if I have questions?',
    paymentHelpAnswer: (): ReactElement => (
      <Text>
        Yes, call Customer Service at{' '}
        {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)}.
      </Text>
    ),
    paymentHelpAnswer1: (): ReactElement => (
      <Text>
        Yes, call Customer Service at{' '}
        {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)},{' '}
        {getInlineBusinessHours(clientName)}.
      </Text>
    ),
    browserRequirementQuestion: 'Browser Requirements',
    browserRequirementAnswer:
      'To login to the Credit Card Account Service you need a browser which is SSL-compliant and has a "secure mode". When your browser is in secure mode, you will notice an unbroken key or lock at the bottom of the page.',
    compatibleBrowserQuestion: 'Compatible Browsers',
    compatibleBrowserQuestion2:
      'We recommend using Chrome for the best user experience. If you are accessing our site with a different web browser, you may not be able to log in or view your account information. Many web browsers are available free for download on the Internet.',
    compatibleBrowserAnswer:
      'If you are accessing our site with a browser other than those recommended above, you may not be able to log in or view your account information. Some of the browsers may be available free for download on the Internet.',
    unavailabilityQuestion:
      'Are there any scheduled times when online customer service is unavailable?',
    unavailabilityAnswer:
      'Our Web server may be down for maintenance purposes daily between midnight and 6 AM EST.',
    interestChargeQuestion: 'How can I avoid interest charges?',
    interestChargeAnswer:
      'If there is no previous balance for the billing period or the sum of your payments and credits for the billing period is at least equal to the previous balance, new purchases and other charges will not incur a interest charge in the billing period when they are made or if they are paid within 26 days of the billing date shown on your statement.',
    latePaymentQuestion: 'How can I avoid late payment fees?',
    latePaymentAnswer:
      'You will not be charged a late payment fee if we receive at least the minimum payment by the payment due date shown on your statement',
    interestChargeAnswer2:
      'You will not be charged an interest fee if you pay your statement balance in full by the due date shown on your statement.',
    customerServiceQuestion: 'Customer Service/Account Information',
    customerServiceAnswer: (): ReactElement => (
      <Text>
        For questions regarding Customer Service or Account Information please
        call our customer service department at{' '}
        {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)},{' '}
        {getInlineBusinessHours(clientName)}.
      </Text>
    ),
    customerServiceLine1: `For questions regarding Customer Service or Account Information please
      call our customer service department at ${creditServiceNumber}, Monday
      through Friday 7AM - 1 AM, Saturday 7AM - 1AM and Sunday 8AM - 12AM
      EST.`,
    customerServiceLine2:
      'Check your internet connection to ensure it is functioning properly',
    customerServiceMessage:
      'You can click on the Secure Message link under your account information box in the upper left corner of the page. Or you can call Customer Service at the phone number below.',
    customerServiceTiming: (): ReactElement => (
      <Text>
        For questions regarding Customer Service or Account Information please
        call our customer service department at
        {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)},{' '}
        {getInlineBusinessHours(clientName)}
      </Text>
    ),
    customerServiceAnswer2: `For questions regarding Customer Service or Account Information please call our customer service department at ${contactUsBrcDc}, 24 hours a day, 7 days a week.`,
    viewingAccountIssueQuestion:
      'What should I do when I am having trouble viewing my account information on the screen?',
    viewingAccountIssueList: (): ReactElement => (
      <Text>
        <p>Please try one or more of the following to resolve the issue:</p>
        <List type="disc">
          <li>Press F5 to refresh your browser</li>
          <li>
            Check that the browser you are using is compatible (see Technical
            Help)
          </li>
          <li>Ensure that pop-up blocker is disabled while on this web site</li>
          <li>
            Check your internet connection to ensure it is functioning properly
          </li>
          <li>
            If you are still encountering problems after trying the above
            actions
          </li>
          <li>
            Please contact our Customer Service department and notify them of
            any problems that you are having in viewing account information by
            calling{' '}
            {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)}{' '}
            {getInlineBusinessHours(clientName)}.
          </li>
        </List>
      </Text>
    ),
    viewingAccountIssueList2: (): ReactElement => (
      <Text>
        <p>Please try one or more of the following to resolve the issue:</p>
        <List type="disc">
          <li>Press F5 to refresh your browser</li>
          <li>
            Check that the browser you are using is compatible (see Technical
            Help)
          </li>
          <li>Ensure that pop-up blocker is disabled while on this web site</li>
          <li>
            Check your internet connection to ensure it is functioning properly
          </li>
          <li>
            If you are still encountering problems after trying the above
            actions
          </li>
          <li>
            Please contact our Customer Service department and notify them of
            any problems that you are having in viewing account information by
            calling {creditServiceNumber} Monday through Friday 7AM - 1 AM,
            Saturday 7AM - 1AM and Sunday 8AM - 12AM EST.
          </li>
        </List>
      </Text>
    ),
    viewingAccountIssueList4: (): ReactElement => (
      <Text>
        <p>Please try one or more of the following to resolve the issue:</p>
        <List type="disc">
          <li>Press F5 to refresh your browser</li>
          <li>
            Check that the browser you are using is compatible (see Technical
            Help)
          </li>
          <li>Ensure that pop-up blocker is disabled while on this web site</li>
          <li>
            Check your internet connection to ensure it is functioning properly
          </li>
        </List>
        <p>
          Please contact our Customer Service department and notify them of any
          problems that you are having in viewing account information by calling
          Credit Services at 1-866-221-1126
        </p>
        <CustomerServiceHours>
          <li>Monday - Friday</li>
          <span>08:00 AM - 10:00 PM EST</span>
          <li>Saturday</li>
          <span>10:00 AM - 07:00 PM EST</span>
          <li>Sunday</li>
          <span>closed</span>
        </CustomerServiceHours>
      </Text>
    ),
    dualCardIssue: (): ReactElement => (
      <Text>
        Please contact our Customer Service department and notify them of any
        problems that you are having in viewing account information by calling{' '}
        {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)}{' '}
        {getInlineBusinessHours(clientName)}
      </Text>
    ),
    OtherTypeIssue: (): ReactElement => (
      <Text>
        Please contact our Customer Service department and notify them of any
        problems that you are having in viewing account information by calling
        Credit Services at{' '}
        {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)}, 24
        hours a day, 7 days a week.
      </Text>
    ),
    viewingAccountIssueList3: (): ReactElement => (
      <Text>
        <p>Please try one or more of the following to resolve the issue:</p>
        <List type="disc">
          <li>Press F5 to refresh your browser</li>
          <li>
            Check that the browser you are using is compatible (see Technical
            Help)
          </li>
          <li>Ensure that pop-up blocker is disabled while on this web site</li>
          <li>
            Check your internet connection to ensure it is functioning properly
          </li>
        </List>
      </Text>
    ),
    closeAccountQuestion: 'What happens if I choose to close my account?',
    closeAccountAnswer1:
      'You are a valued member and we hope you will choose not to close your account. At your request, we will close your account and report your decision to the credit reporting agencies as "Closed, by customer request". Remember that both you and any Personal Guarantor are still responsible for any remaining balance on the account. You can close child/sub accounts online without effecting the status of the parent/main account.',
    closeAccountAnswer2:
      'You are a valued member and we hope you will choose not to close your account. At your request, we will close your account and report your decision to the credit reporting agencies as "Closed, by customer request". Remember that both you and any Personal Guarantor are still responsible for any remaining balance on the account.',
    phoneNumberUpdateQuestion:
      'How do I update my mail address and/or phone number?',
    phoneNumberUpdateAnswer: `You can update your company's mailing address and phone number by clicking the Update Account Information on the Main Menu.`,
    legalNameChange: 'Legal Name Change',
    LegalNameChangeText:
      'Please mail a copy of the legal documents to the following address, in order to process your request:',
    LegalNameChangeText2: (): ReactElement => (
      <>
        To change the company&apos;s legal name on the account contact Customer
        Service at{' '}
        {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)}
      </>
    ),
    legalNameChangeText3: (): ReactElement => (
      <>
        To change the company&apos;s legal name on the account contact Customer
        Service at{' '}
        {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)}.
        <CustomerServiceHours>
          <li>Monday - Saturday</li>
          <span>09:00 AM - 10:30 PM EST</span>
          <li>Sunday</li>
          <span>09:00 AM - 09:00 PM EST</span>
        </CustomerServiceHours>
        <br />
        <Text>
          Please mail a copy of the legal documents to the following address, in
          order to process your request:
        </Text>
        <AddressWrapper>
          <p>{displayName} BRC</p>
          <p>{brcLegalNameChange.addressLine1}</p>
          <p>
            {brcLegalNameChange.city}, {brcLegalNameChange.state} <br />
            {brcLegalNameChange.zipCode}
          </p>
        </AddressWrapper>
      </>
    ),
    legalNameChangeText4: (): ReactElement => (
      <>
        To change the company&apos;s legal name on the account contact Customer
        Service at{' '}
        {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)}, 24
        hours a day, 7 days a week.
        <br />
        <Text>
          Please mail a copy of the legal documents to the following address, in
          order to process your request:
        </Text>
        <AddressWrapper>
          <p>
            {`${displayName} ${
              (!shouldShowQuestion(clientName) && 'LBA') || ''
            } BRC`}
          </p>
          <p>PO BOX 71726</p>
          <p>
            {cities.philadelphia}, {stateList.pennsylvania} <br />
            19176-1726
          </p>
        </AddressWrapper>
      </>
    ),
    phoneNumberUpdateAnswer2: `You can update your company's mailing address and phone number by selecting “Edit Business Information” under the “Account Management” section.`,
    phoneNumberUpdateAnswer3: `You can update your company's mailing address and phone number by clicking on the Account Management icon at the top of the page, in the Other Services box on the bottom right side of the page, click on the Update Account Information link.`,
    updateCompanyAddress: (): ReactElement => (
      <>
        <Text>
          To change the company&apos;s legal name on the account contact
          Customer Service at{' '}
          {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)}{' '}
          Monday through Friday 7AM - 1 AM, Saturday 7AM - 1AM and Sunday 8AM -
          12AM EST.
        </Text>
        <Text>
          Please mail a copy of the legal documents to the following address, in
          order to process your request:
        </Text>
        <AddressWrapper>
          <p>Legal Name Change</p>
          <p>{brcLegalNameChange.addressLine1}</p>
          <p>
            {brcLegalNameChange.city}, {brcLegalNameChange.state}{' '}
            {brcLegalNameChange.zipCode}
          </p>
        </AddressWrapper>
      </>
    ),
    creditBureauQuestion: 'Credit Bureau Questions/Disputes',
    creditBureauAnswer1:
      'If you believe that we have reported inaccurate information about you to a customer reporting agency, please write to us at the following address:',
    creditBureauAnswer2:
      'In doing so, please identify the accurate information and tell us why you believe it is incorrect. If you have a copy of the credit report that includes the accurate information, please send a copy of that report to us as well.',
    creditIncreaseQuestion: 'How do I increase my available credit online?',
    creditIncreaseAnswer:
      'You can request an increase to your available credit online by clicking Apply for increase to Available Credit on the Main Menu. Please be advised that your application for an increase is not considered received untill you click the Submit Application button at the end of the process.',
    dualCardCreditLineIncrease: (): ReactElement => (
      <Text>
        In order to request a credit line increase, please call Credit Services
        at {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)}, 24
        hours a day, 7 days a week.
      </Text>
    ),
    otherCardCreditLineIncrease:
      'You can request an increase to your available credit online by selecting “Account Management” and then under “Request Credit Line Increase” follow the instructions. Please be advised that your application for an increase is not considered received until you click the Submit Request button at the end of the process.',
    creditBalanceRefundQuestion: 'How Do I Request a Credit Balance Refund?',
    creditBalanceRefundAnswer:
      'You can request a credit balance refund online by clicking Request Refund for a Credit Balance on the Main Menu.',
    taxExemptionQuestion: 'How do I apply for sales tax exemption?',
    taxExemptionAnswer: `Take your state issued "certificate of tax exemption" to
        any ${displayName} store location in the state purchases are exempted from
        sales tax and apply at Customer Service for a "tax exempt
        account". When applying for a tax exempt account, please
        provide your ${payableAccountAcronym} account number to ${displayName}
        customer service associate and request the tax exempt account linked
        to the ${payableAccountAcronym}
        account.`,
    'To ensure you are not charged tax on your purchases, you must show a copy of a valid Tax-Exemption certificate at the time of each purchase. To learn more about obtaining a %displayName% Tax-Exempt Account ID, please visit':
      'To ensure you are not charged tax on your purchases, you must show a copy of a valid Tax-Exemption certificate at the time of each purchase. To learn more about obtaining a %displayName% Tax-Exempt Account ID, please visit',
    'or visit the Pro Desk in your local %displayName% Store. To obtain a credit for sales tax charged on a previous purchase, bring the original sales receipt to the Pro Desk at the %displayName% location where you made the purchase.':
      'or visit the Pro Desk in your local %displayName% Store. To obtain a credit for sales tax charged on a previous purchase, bring the original sales receipt to the Pro Desk at the %displayName% location where you made the purchase.',
    onlineMessageCenterQuestion: 'What is the Online Message Center?',
    onlineMessageCenterAnswer:
      'The Online Message Center provides a secure online method for you to send requests or inquiries about your account to Customer Service. To access the Online Message Center click on “send us a secure message”.',
    inquriesQuestion: 'How can I view responses to my requests or inquiries?',
    inquriesAnswer:
      'Responses to your inquiries can only be viewed through the Online Message Center. You will be sent a notice to your personal email address upon receipt of your inquiry and when a response is posted.',
    onlineMessageSecureQuestion:
      'Is the information I submit through the Online Message Center secure?',
    onlineMessageSecureAnswer:
      'Yes. Information submitted through the Online Message Center is secure and safe. This service is only accessible through the Online Credit Center.',
    questionType:
      'What type of questions can I submit through the Online Message Center?',
    questionTypeLine1:
      'The Online Message Center should be used for questions concerning:',
    questionTypeList: (): ReactElement => (
      <List type="disc">
        <li>
          Account Information
          <p>Available Credit, Receipt of Payment, Privacy Preference</p>
        </li>
        <li>
          Account Maintenance
          <p>
            Current Balance, Credit Line Increase, Lost/Stolen Card, Replacement
            Credit Card, Name/Address Change, Add/Remove Authorized User
          </p>
        </li>
        <li>
          Billing Inquiries
          <p>
            {' '}
            Credit Bureau Report, Billing Date, Payments, Promotional Purchases,
            Interest Charges, Late Fees, Credit Balance Inquiry
          </p>
        </li>
        <li>
          Disputes
          <p>Purchase Dispute, Insurance Charges</p>
        </li>
        <li>Technical Support, eStatements, e-Pay, Password/Login Issues</li>
        <li>Other</li>
      </List>
    ),
    invoicesDisputeQuestion: 'I need to dispute an unauthorized invoice/sale.',
    invoicesDisputeLine1:
      'If you are disputing an unauthorized sale, please print the provided questionnaire and mail or fax the completed form to:',
    invoicesDisputeLine2: (): ReactElement => (
      <Text>
        If you are disputing a sale for reasons other than unauthorized use such
        as sales tax, merchandise returns, etc, Please call Credit Services at{' '}
        {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)}.
      </Text>
    ),
    invoicesDisputeLine3:
      'To ensure prompt resolution, please complete the form in its entirety.We must hear from you no later than 60 days after we sent you the first bill on which the invoice appeared.You do not have to pay the invoice in dispute while we are investigating, but you are still obligated to pay the remaining invoices that are not in dispute.While we investigate the invoice in question, we cannot report you as delinquent, or take any action to collect the invoice in dispute.',
    invoicesDisputeLine4:
      'We will investigate the dispute and advise you of the results within 60 days.',
    'Fax:': 'Fax:',
    downloadInvoicesQuestion: 'How can I download invoices?',
    downloadInvoicesAnswer: (): ReactElement => (
      <Text>
        <List>
          <li>
            Under &ldquo;Billing&rdquo; in the navigation bar&cedil; find
            &ldquo;
            <QuestionLink to={routePaths.invoice}>Invoices</QuestionLink>
            &rdquo; and click.
          </li>
          <li>
            On that page above the invoice listing is a &ldquo;Download
            Invoices&rdquo; button.
          </li>
          <li>
            Select whether to include open or closed invoices&cedil; a date
            range&cedil; and choose a file format. Then you can press the
            &ldquo;Download&rdquo; button to download your invoices.
          </li>
        </List>
      </Text>
    ),
    documentationProofQuestion:
      'How do I obtain proof of purchase documentation?',
    documentationProofAnswer:
      'You can view and print details for purchases made within the past 90 days. The details will include the date of purchases, purchases description and much more.',
    accountActivityPastQuestion:
      'How do I view and print my past account activity?',
    accountActivityPastAnswer:
      'You can view and print details for purchases made within the past 90 days. The details will include the date of purchases, purchases description and much more.',
    downloadInvoicesAnswer2: (): ReactElement => (
      <List>
        <li>Click &ldquo;Account History&rdquo; at the top of the page.</li>
        <li>
          Then on the History Page&cedil; click&ldquo;Invoices&rdquo; on the
          left side of the page.
        </li>
        <li>
          On that page below the tabs is a &ldquo;Download Invoices&rdquo; link.
        </li>
        <li>
          That brings up a &ldquo;Download a range of invoices&rdquo; pop up
          window.
        </li>
        <li>
          On that window&cedil; select &ldquo;Open Invoices&rdquo;&cedil;
          &ldquo;Closed Invoices&rdquo; or &ldquo;Open and Closed
          Invoices&rdquo;&cedil; a date range&cedil; and format. Then click the
          Download button
        </li>
      </List>
    ),
    documentationProofAnswer2:
      'Under the “Billing” section select “Transactions” or “eStatements” to view and print details for transactions made within the past 6 months. The details will include the date of purchases, purchase description and much more.',
    downloadStatementQuestion: 'How can I download Statements?',
    downloadStatementAnswer: (): ReactElement => (
      <List>
        <li> Select “Billing” at the top of the page</li>
        <li>
          Select “eStatements” and choose the statement you wish to
          view/download
        </li>
        <li>Select the desired “Statement Date”</li>
        <li>Select the “PDF” button</li>
      </List>
    ),
    documentationProofAnswer3:
      'You may view and print details for purchases made. The details will include: date and amount of purchase, reference number, and Store name. Click on the Account History icon at the top of the page.',
    accountActivityPastAnswer2:
      'You may view and print a listing of activity on your account. This includes purchases, payments, returns and interest charges. Click on the Account History icon at the top of the page.',
    cardLostQuestion:
      'What should I do if my credit card has been lost or stolen?',
    cardLostAnswer: (): ReactElement => (
      <Text>
        Contact Customer Service at{' '}
        {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)}
      </Text>
    ),
    cardMisplacedQuestion:
      'What should I do if my credit card has been damaged or misplaced?',
    cardMisplacedAnswer: `You can order a replacement card by clicking "Add Authorized
      Buyers" from the main account summary screen. On the add
      authorized buyers screen&cedil; click "Replacement Card".
      Then select the buyer that needs the card and click "Order
      Replacement Card".`,
    replacementCardNotReceived:
      'What should I do if I have not received my replacement card?',
    cardLostAnswer2: (): ReactElement => (
      <Text>
        You can report your card lost, misplaced, or stolen online by selecting
        “Request Cards for Additional Buyers” under the “Account Management”
        section, and then select the “Report a lost or stolen card.” Please
        review your account for any purchase you did not make. If you see any
        unauthorized charges please call Customer Service immediately at{' '}
        {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)}.
      </Text>
    ),
    creditCardDamagedQuestion:
      'What should I do if my credit card has been damaged?',
    creditCardDamagedAnswer: (): ReactElement => (
      <Text>
        You can request a replacement card by selecting “Request Cards for
        Additional Buyers” under the “Account Management” section, then follow
        the instructions to request new cards for additional buyers. Or you may
        call Customer Service at{' '}
        {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)}.
      </Text>
    ),
    creditCardLostQuestion:
      'What should I do if my credit Card has been lost, misplaced, stolen, or damaged?',
    creditCardLostAnswer: (): ReactElement => (
      <Text>
        Call customer service at{' '}
        {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)},{' '}
        {getInlineBusinessHours(clientName)}.
      </Text>
    ),
    creditCardLostAssistance: (): ReactElement => (
      <Text>
        Call customer service at{' '}
        {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)}.
        <CustomerServiceHours>
          <li>Monday - Saturday</li>
          <span>9:00 AM - 10:30 PM EST</span>
          <li>Sunday</li>
          <span>9:00 AM - 09:00 PM EST</span>
        </CustomerServiceHours>
      </Text>
    ),
    padLockQuestion:
      'Am I on a secure site even if the padlock on my browser is not locked?',
    padLockAnswerLine1: `Your account information, credit application and other sensitive
      information is being encrypted on the Online Credit Center. An
      encrypted item is scrambled so that no one between your computer and
      Synchrony Bank(SYNCB) can read the message or information you entered.`,
    padLockAnswerLine2: `The reason the padlock icon provided on your browser may not appear
      locked (symbolizing an encrypted session) is because the Online Credit
      Center may use multiple frames to display information on your
      computer. The graphical header and navigation bar across the top of
      your screen may be one frame. The selections that appear on the left
      are another frame. The main portion of the site appears in the
      remaining frame. Frames that display general information about the
      site, such as the navigation bar across the top of your
      screen, are not encrypted. However, any screen that
      displays or requests information about your account, user
      name, passcode, application and other sensitive
      information is encrypted.`,
    sslQuestion: 'What is SSL?',
    sslAnswer: `SSL is an abbreviation for Secured Socket Layer. SSL technology
        secretly encodes (or encrypts) information as it is being sent over
        the Internet between your computer and SYNCB's systems. This
        encryption technology helps ensure that your account information
        travels securely over the internet and remains confidential`,
    secureSessionQuestion:
      'What is a "secure session" and how does it help keep my information safe?',
    secureSessionAnswer: `During a secure SSL(Secured Socket Layer) session, data
      traveling between your computer and SYNCB's systems is secured
      using encryption technology. This SSL technology secretly encodes your
      information to create a private conversation that only your computer
      and SYNCB's systems can understand.`,
    viewingAccountInfo:
      'What should I do when I have finished viewing my account information?',
    viewingAccountInfoAnswer:
      'When you have finished using a secure area of Online Credit Center, make sure you close/exit the internet browser software (e.g. Firefox, Internet Explorer, AOL). This will decrease the possibility of anyone viewing confidential account information.',
    publicComputerUsage: 'What should I do if I am using a "public" computer?',
    publicComputerUsageAnswer: `If you use a computer that others also work on and you are
          uncomfortable that they may view "cached" pages after you
          have left the station, you should close/exit the internet
          browser software (e.g. Firefox, Internet Explorer,
          AOL) before leaving. This will decrease the possibility of anyone
          viewing confidential account information.`,
    secureConnection: 'How can I tell when my connection is secure?',
    secureConnectionAnswer1:
      'To confirm that your information is encrypted perform the following steps:',
    secureConnectionList: (): ReactElement => (
      <ol>
        <li>
          Place your cursor on an area of screen where confidential information
          is being displayed.
        </li>
        <li>
          (a). With an Internet Explorer browser, right-click, select
          &lsquo;Properties&apos;.
          <br />
          (b). With a Firefox browser, right-click, select &lsquo;View Frame
          Info&apos;&rsquo; or select &lsquo;View&rsquo; | &lsquo;Page Frame
          Info&rsquo; from the browser menu bar.
          <br />
          (c). For Macintosh users&cedil; Ctrl-click&cedil; select &lsquo;New
          Window with this Frame&rsquo; OR select &lsquo;View&rsquo; |
          &lsquo;Page Info&rsquo; from the browser menu bar.
        </li>
        <li>
          Check the Web address that you have accessed. If you are in a secure
          area, the address will appear as https://www. Notice the
          &ldquo;s&rdquo; in the address. This means that you have accessed a
          secure server.
        </li>
      </ol>
    ),
    samsRewards: (): ReactElement => (
      <p>
        See your &ldquo;
        <Link
          isInline
          href="https://scene7.samsclub.com/is/content/samsclub/sams-cash-with-mastercard"
          target="_blank"
          rel="noopener noreferrer"
        >
          How to Earn Sam’s Cash with Your Sam’s Club® Mastercard®
        </Link>
        &rdquo; rewards program terms for details.
      </p>
    )
  },
  autoPayTermsAndConditions: {
    title: 'Synchrony Bank Autopay Authorization',
    introduction:
      'This Synchrony Bank Autopay Authorization (“Authorization”) governs the recurring electronic payments that you seek to arrange on your credit card account (“Account”) with Synchrony Bank (together with our agents, “we,” our” or “us”). This Authorization does not amend any of the terms of your Account agreement. By signing this Authorization, you agree to be bound by the terms of this Authorization. We will notify you by mail or email when the Autopay program has been established for your Account. Until then, please continue to make payments on your Account as you normally do. If you have any questions, please call our customer service at the phone number on the back of your credit card or on your billing statement. Please save a copy of this Authorization for your records. The Authorization we have on file with the latest effective date supersedes and replaces any previous authorizations.',
    authorize:
      'You authorize us to initiate electronic payments from the checking account (“Deposit Account”) you have specified or any substitute Deposit Account you later specify as set forth below.',
    amountsSubtitle:
      'Description of amounts to be withdrawn based on your selection:',
    totalAmountTitle: 'A.	TOTAL MINIMUM PAYMENT DUE',
    totalAmountDescription:
      'as shown on your monthly billing statement. If you choose this option, the amount of each electronic payment will be either the Total Minimum Payment Due on your most recent billing statement, or if payments have posted to your Account since the closing date of your most recent billing statement, the Total Minimum Payment Due less any such payments.',
    fullStatementBalanceTitle: 'B.	FULL STATEMENT BALANCE',
    fullStatementBalanceDescription:
      'as shown on your monthly billing statement as your New Balance. If you choose this option, the amount of each electronic payment will be the New Balance shown on your most recent billing statement, or if payments have posted to your Account since the closing date of your most recent billing statement, the New Balance less any such payments.',
    fullStatementBalanceExampleTitle:
      "Here's an example of the Full Statement Balance option:",
    yourStatement: '$1,000.00 New Balance (as shown on your statement)',
    yourPayment: '(-) 100.00 Additional Payment (paid after your closing date)',
    yourTotalStatement:
      '$900.00 Recurring Payment Amount deducted from your Deposit Account',

    otherAmountTitle: 'C.	OTHER AMOUNT you specify.',
    otherAmountDescription: `If you choose this option, the amount of each electronic payment will be the amount you have specified (or any such amount you later specify). If payments have posted to your Account since the closing date of your most recent billing statement, the electronic payment will be the Other Amount less any such payments. Please note if you enroll after your statement closing date but before your payment due date, and you were not previously enrolled on the closing date of your most recent billing statement, your payment(s) posted since the closing date of your most recent billing statement will not affect the amount of your first Autopay payment.
    If the Other Amount is less than the Total Minimum Payment Due, the amount of the electronic payment will be the Total Minimum Payment Due as set forth in section A above.`,
    notExceedBalance:
      'In no event will the amount of any electronic payment exceed the balance due on your Account.',
    checkingPaymentsDeductedTitle:
      '1. Checking account from which payments are deducted.',
    checkingPaymentsDeductedDescription: (): ReactElement => (
      <>
        The Deposit Account you specify (or any substitute Deposit Account you
        later specify) must be owned by you and maintained at a valid financial
        institution in the United States. You may change or update the Deposit
        Account through the Payment Methods screen of our website or by mailing
        in a new form.
        <strong>
          You authorize us to update your Deposit Account information upon
          receipt of a notice of change from the financial institution holding
          your Deposit Account.
        </strong>
      </>
    ),

    dateOfPaymentTitle: '2. Date of payment. ',
    dateOfPaymentDescription: (): ReactElement => (
      <>
        We will initiate the electronic payment on or after the due date
        reflected on your statement, as it may be modified by you or us from
        time to time. In the event that your scheduled due date is a weekend or
        holiday, your payment will be credited to your Account on or before the
        scheduled{' '}
        <strong>
          due date but may not be initiated (or debited from your Deposit
          Account)
        </strong>{' '}
        until after the due date.
        <Inset all="base">
          <Body weight="semiBold" isInline>
            Promotional balances.{' '}
          </Body>
          <Body isInline>
            If you have a promotional purchase balance, choosing to have the
            electronic payment equal the Total Minimum Payment Due or Other
            Amount will not necessarily result in your promotional purchase
            balance(s) being paid in full by the promotion expiration date. If
            you want to pay your promotional balance in full before it expires,
            you should arrange to make additional payments or ensure that the
            Other Payments you specify are sufficient. Please see your billing
            statement for promotional purchase balances and other Account
            details. In the event that you choose the Full Statement Balance
            option and have a promotional balance, the amount of the electronic
            payment will include the promotional balance. If you have chosen
            Full Statement Balance option and do not want the promotional
            balance to be included in the electronic payment amount, you need to
            change your payment option.
          </Body>
        </Inset>
      </>
    ),
    overlimitAmountsTitle: '3. Overlimit amounts.',
    overlimitAmountsDescription:
      'If your statement balance ever exceeds your credit limit, you should make a payment to reduce your statement balance below your credit limit. You should not rely on the electronic payments authorized in this Authorization to bring your Account balance within your credit limit. In the event that you make such a payment after your Closing Date, we will adjust the amount of your recurring payment as described previously. ',
    modificationTitle: '4. Modification.',
    modificationDescription:
      'You may modify your Autopay elections. Please note when you make changes to a payment amount that was in effect on your last statement closing date, and you make that change between 6pm ET on your statement closing date and 11:59pm ET on your next payment due date, (1) your new Autopay payment amount selection will not go into effect until after your next payment due date; and (2) the amount of the Autopay payment that will be made on your next payment due date will be based on the Autopay selection at the time of your last statement closing date. Changes to your funding information made after 6pm ET on your payment due date (or if your payment due date is on a Saturday, after 6pm ET on the Friday before) will not take effect until your next payment due date.',
    cancellationTitle:
      '5. Cancellation. You may cancel or revoke this Authorization at any time',
    cancellationDescription: `by selecting "Cancel Autopay" on the website or by calling customer service at the phone number on the back of your credit card or on your billing statement. Cancellation may take up to two business days. You will be notified by mail or email when the cancellation process is complete.
    If between 6pm ET on your statement closing date and 11:59pm ET on your next payment due date you both cancel an Autopay that was in effect on your last statement closing date and re-enroll in Autopay, (1) your new Autopay payment amount selection will not go into effect until after your next payment due date; (2) the amount of the Autopay payment that will be made on your next payment due date will be based on the Autopay selection at the time of your last statement closing date; and (3) changes to your funding information made after 6pm ET on your payment due date (or if your payment due date is on a Saturday, after 6pm ET on the Friday before) will not take effect until your next payment due date.`,
    errorsTitle: '6. Errors and returned payments.',
    errorsDescription:
      'In the event that we make an error in processing any electronic payment described above, you authorize us to initiate an electronic debit or credit to your Deposit Account to correct the error. In the event that any electronic payment is returned unpaid, you authorize us to reinitiate it up to two times as permitted by law and applicable network rules.',
    understandSubtitle: 'You understand that:',
    amountMayVary:
      'The amount of the electronic payment may vary each month and if you choose the Total Minimum Payment Due or Full Statement Balance, your monthly billing statement may be your only notice of the payment amount.',
    whileEnrolled:
      'While enrolled in the Autopay program you may make an additional payment at any time.',
    ifNoBalance:
      'If your Account balance is equal to or less than zero, no electronic payment will be processed.',
    ifPaymentUnpaid:
      'If your payment is returned unpaid, your Account may be subject to a late payment fee.',
    weMayCancelEnrollment:
      'We may cancel (but are not obligated to) your enrollment in the Autopay program if your bank returns a payment unpaid. We may also cancel your enrollment in the program in our sole discretion. You will receive an emailed or mailed cancellation notification if we cancel your enrollment.',
    enrollmentDetails:
      'If your electronic payments request is approved, your Account will be enrolled in the Autopay program. Once enrolled, you may change the designated Deposit Account by updating your Deposit Account information on the website or sending in a new form. If you do not update your Deposit Account information and your designated Deposit Account is no longer valid or your payment is returned for an invalid account number, your enrollment in the Autopay program may be cancelled.'
  },
  messages: {
    newMessage: 'New Message',
    messageTo: 'Message To',
    subject: 'Subject',
    selectSubjectAccount: 'select subject account',
    noMoreThan500Characters:
      'Your message may contain no more than 500 characters.',
    messageIsRequired: 'Message is required.',
    charactersOverLimit: '%charactersOverLimitNum% characters over limit',
    charactersRemaining: '%charactersRemainingNum% characters remaining',
    'Received Messages': 'Received Messages',
    'Sent Messages': 'Sent Messages'
  },
  gridPagination: {
    previousPage: 'PREVIOUS PAGE',
    page: 'Page',
    go: 'Go',
    nextPage: 'NEXT PAGE',
    showing: 'Showing',
    of: 'of'
  },
  requestAdditionalBuyer: {
    requestCardTitle: 'Request Additional Buyer Cards',
    successfulRequestBanner:
      'cards were successfully requested and will be sent to the business address on file.',
    cardLostAndStolen: 'Has a card been lost or stolen?',
    reportLostAndStolenCardLink: 'Report a lost or stolen card',
    reportLostAndStolenCardsText:
      'so that we can close the account and issue you a new account number.',
    businessAddress:
      'Requested buyer cards will be sent to the following business address in 10 business days:',
    noOfCardNeeded: 'How many cards are needed?',
    'Enter Number of Cards': 'Enter Number of Cards',
    requestDescription:
      'Due to mailing constraints, you may request up to 9 cards at a time. If you need additional cards, we can accomodate one request per day, so please request the rest of the needed cards tomorrow, or contact Credit Services.',
    requestCards: 'REQUEST CARDS',
    errorMessage:
      'We are sorry, we have technical difficulties in processing your request: Code –',
    errorMessageText: (): ReactElement => (
      <>
        , please contact {clientName} credit services at{' '}
        {getCSRNumber(userAccountInfo.isProx, userAccountInfo.cardType)} to
        assist you further
      </>
    )
  },
  creditServicesList: {
    // list heading
    'Call %phone% for assistance with': 'Call %phone% for assistance with:',

    // common list items. (Refer to brand translations file for brand specific items)
    'Change in Language Preference': 'Change in Language Preference',
    'Change in Billing Date': 'Change in Billing Date',
    'Add/Remove Authorized Buyers': 'Add/Remove Authorized Buyers',
    'Requesting Replacement Cards': 'Requesting Replacement Cards',
    'Report Card Lost or Stolen': 'Report Card Lost or Stolen',
    'Request Credit Line Increase': 'Request Credit Line Increase',
    'Request a Credit Limit Change': requestLimitChange,
    'Request Refund of Credit Balance': 'Request Refund of Credit Balance',
    'Closing an Account': 'Closing an Account'
  },
  lostCardConfirmation: {
    'Card successfully reported as lost/stolen':
      'One or more cards were successfully reported as lost/stolen.',
    'Your account has been closed': 'Your account has been closed.',
    'Next steps': 'Next steps:',
    'You will not be able to make new purchase':
      'You will not be able to make new purchases and the account will no longer be accessible online. You will be issued a new account number and your new card will be mailed to the business address on file within 10 business days.',
    'Register for online access as new user':
      'When you receive your new card, you will need to register for online access as a new user.',
    'Request new auth code': `Once you receive your new card, request a new authentication code from ${displayName} Credit Services at ${creditServiceNumber}.`
  },
  transactions: {
    statementToExpand:
      'Select a statement to expand it and view itemized transaction details.',
    viewTransactionInFormats:
      'You may view transactions from the previous 6 months, and download transactions as CSV, Excel, Quicken, and QuickBooks files from the previous 4 months.',
    note: 'Note: You can only download transaction details on desktop devices.'
  },
  recoverUserId: {
    'Recover User Id': 'Recover User ID',
    'your User ID is': ', your User ID is:',
    'Do you remember your password': 'Do you remember your password? If not,',
    'click here to reset it': 'click here to reset it.',
    'To recover a lost User ID, please provide the following information associated with the online profile':
      'To recover a lost User ID, please provide the following information associated with the online profile.',
    'The information entered does not match our records':
      'The information entered does not match our records. Please try again.',
    'Return to Login': 'Return to Login'
  },
  // Welcome mat is optionally rendered on Account Summary page after initial login
  welcomeMat: {
    // heading content
    'Welcome to %brandName% Online Service':
      'Welcome to %brandName% Online Service',

    // first feature
    'Comfortably view the site on your mobile device, tablet, laptop, or desktop computer.':
      'Comfortably view the site on your mobile device, tablet, laptop, or desktop computer.',

    // second feature
    'Find the info you need with searching and sorting capabilities.':
      'Find the info you need with searching and sorting capabilities.',

    // third feature
    'Have questions? Check out the': 'Have questions? Check out the',
    'for FAQs.': 'for FAQs.',

    // content after the features
    'If you experience any issues or have additional questions, please send us a':
      'If you experience any issues or have additional questions, please send us a',
    'Secure Email': 'Secure Email',
    'or contact %csrName% Credit Services at %csrNumber%.': ({
      csrName,
      csrNumber
    }: {
      csrName: string
      csrNumber: string
    }) => {
      return (
        <>
          or contact {csrName} at{' '}
          <Body isInline weight="bold">
            {csrNumber}
          </Body>
          .
        </>
      )
    }
  },
  mfaContactModal: {
    'Your Contact Information': 'Your Contact Information',
    "Let's verify your contact information.":
      "Let's verify your contact information.",
    'To protect your account, please verify your contact information.':
      "To protect your account, please verify your contact information. Provide an email address and phone number only you can access. We'll use it to verify your identity for future logins.",
    'update my information': 'update my information',
    'my information is correct': 'my information is correct'
  },
  mfaConsent: {
    'You authorize Synchrony Bank...':
      'You authorize Synchrony Bank to contact the phone number you have provided. By providing a cell phone number, you agree to receive Account updates and information, including text messages from %brandName% and Synchrony Bank.'
  },
  updateContactInformation: {
    'Your contact information has been updated successfully.':
      'Your contact information has been updated successfully.',
    'To keep your business account secure, we dont allow changes to be made online to First Name and Last Name.': `To keep your business account secure, we don't allow changes to be made online to First Name and Last Name.`,
    'Please contact': 'Please contact',
    'to update them.': 'to update them.',
    'Your contact information has been updated successfully': `Your contact information has been updated successfully.`
  },
  paymentMethodForm: {
    Optional: 'Optional',
    'Add Payment Method': 'Add Payment Method',
    'Make this my default payment method':
      'Make this my default payment method',
    'The routing number and account number are located below...':
      'The routing number and account number are located below the Memo field in the lower left corner.',
    'All fields are required unless marked optional.':
      'All fields are required unless marked optional.'
  },
  cardActivation: {
    'Choose An Authentication Method': 'Choose An Authentication Method',
    'Use an option below to confirm who you are.':
      'Use an option below to confirm who you are.',
    'Last 4-digits of SSN': 'Last 4-digits of SSN',
    'We can only accept the last 4 digits...': `We can only accept the last 4 digits of the Personal
                      Guarantor’s SSN or the Beneficial Owner’s SSN.`,
    'Who is the Personal Guarantor / Beneficial Owner?':
      'Who is the Personal Guarantor / Beneficial Owner?',
    'Security Code from old card': 'Security Code from old card',
    'Provide us with the Security Code...': `Provide us with the Security Code from your MyLowe's Pro Rewards Credit Card, the one that is currently active.`
  }
}

export default strings
